import React, { useState, useEffect } from 'react';
import { ShareSimulatorModal } from '../ShareSimulatorModal';
import Papa from 'papaparse';
import 'leaflet/dist/leaflet.css';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import { X, Users, AlertCircle, CheckCircle, Clock, Download, FileText, BarChart3, Building2, Save, Trash2, Receipt, TrendingUp, ChevronLeft, ChevronRight, Pencil, Check, Loader2, Upload, Calculator, Sun, Plus, Copy, MapPin, Play, ClipboardPaste } from 'lucide-react';
import { AreaChart, Area, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { supabase } from '../../lib/supabase';
import { toast } from 'react-hot-toast';

const CONSUMER_SHEET_URL = 'https://docs.google.com/spreadsheets/d/1zRepz8BxnkJS5VUKt1ZUl5mUYWkCKzCX/export?format=csv&gid=958505389';
const PRODUCER_SHEET_URL = 'https://docs.google.com/spreadsheets/d/1BXDcjal6X6Ka6yqKb0YXiFJw7JFPvzsW/export?format=csv&gid=291383707';

const NETWORK_REDUCTION_RATES: Record<string, number> = {
  bruxelles_A: 80.47,
  bruxelles_B: 46.59,
  bruxelles_C: 21.44,
  bruxelles_D: 0,
  wallonie_batiment: 108,
  wallonie_communaute: 0,
};

const SHARING_TYPE_DESCRIPTIONS: Record<string, string> = {
  A: 'Même bâtiment',
  B: 'Même cabine Sibelga',
  C: 'Même poste Elia',
  D: 'Poste Elia différent',
};

function getNetworkReductionRate(region: string, sharingType: string, sharingScope: string): number {
  if (region === 'bruxelles' && sharingType) {
    return NETWORK_REDUCTION_RATES[`bruxelles_${sharingType}`] ?? 0;
  }
  if (region === 'wallonie' && sharingScope) {
    return NETWORK_REDUCTION_RATES[`wallonie_${sharingScope}`] ?? 0;
  }
  return 0;
}

type SimProfileValue = string;
type SimProducerProfileValue = string;

interface SimParticipant {
  id: string;
  label: string;
  type: 'consumer' | 'producer' | 'mixed' | 'prosumer' | 'consumer_prosumer';
  profileType: SimProfileValue;
  producerProfileType: SimProducerProfileValue;
  annualConsumption: string;
  annualInjection: string;
  consumptionFile: File | null;
  injectionFile: File | null;
  consumptionFileName: string;
  injectionFileName: string;
  // Raw QH values stored directly in DB to avoid CSV round-trip corruption
  consumptionQhValues?: number[] | null;
  injectionQhValues?: number[] | null;
  supplierBuyRate: string;
  communityRate: string;
  supplierSellRate: string;
  communityBuyRate: string;
  address: string;
  prosumerInstalledPower: string;
  prosumerSiteConsumption: string;
  prosumerConsumptionProfileType: SimProfileValue;
}

interface SimResultData {
  totalShared: number;
  totalConsumption: number;
  totalProduction: number;
  totalGrossConsumption: number;
  totalGrossProduction: number;
  totalAutoConsumption: number;
  totalGains: number;
  totalCommodityGains: number;
  totalNetworkGains: number;
  networkReductionRate: number;
  hasGains: boolean;
  monthlyData: { month: string; consumption: number; production: number; shared: number; gains: number; commodityGains: number; networkGains: number }[];
  participantMonthlyData: { participantId: string; label: string; type: 'consumer' | 'producer' | 'mixed' | 'prosumer' | 'consumer_prosumer'; monthlyShared: number[]; monthlyBought: number[]; monthlySold: number[]; totalShared: number; totalBought: number; totalSold: number; gains: number; commodityGains: number; networkGains: number; annualConsumption: number; annualProduction: number; grossConsumption: number; grossProduction: number; autoConsumption: number; supplierBuyRate: number; communityRate: number; address: string }[];
}

interface SimQHParticipant {
  participantId: string;
  label: string;
  type: 'consumer' | 'producer' | 'mixed' | 'prosumer' | 'consumer_prosumer';
  consumption: number[];
  production: number[];
  shared: number[];
}

interface SimulationRecord {
  id: string;
  name: string;
  role: 'consumer' | 'producer';
  inputs: Record<string, string | number>;
  results: SimResultData | Record<string, string | number>;
  reduction_rate: number;
  participants: SimParticipant[];
  status: 'draft' | 'complete';
  created_at: string;
  created_by_email?: string | null;
  region?: 'bruxelles' | 'wallonie' | '';
  sharing_type?: 'A' | 'B' | 'C' | 'D' | '';
  sharing_scope?: 'batiment' | 'communaute' | '';
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  /** Conservé pour compatibilité avec les appels existants, non utilisé par le simulateur. */
  operationId?: string | null;
  guestMode?: boolean;
  isSuperAdmin?: boolean;
  guestCountdown?: { daysLeft: number; hoursLeft: number };
}

const SIM_CSV_BUCKET = 'simulation-csv-files';

function simCsvPath(simId: string, participantId: string, kind: 'conso' | 'injection', fileName: string): string {
  return `${simId}/${participantId}_${kind}_${fileName}`;
}

async function uploadSimCsv(simId: string, participantId: string, kind: 'conso' | 'injection', file: File): Promise<string> {
  const path = simCsvPath(simId, participantId, kind, file.name);
  const { error } = await supabase.storage.from(SIM_CSV_BUCKET).upload(path, file, { upsert: true });
  if (error) throw error;
  return path;
}

async function downloadSimCsvAsFile(path: string, fileName: string): Promise<File> {
  const { data, error } = await supabase.storage.from(SIM_CSV_BUCKET).download(path);
  if (error) throw error;
  return new File([data], fileName, { type: data.type });
}

async function deleteSimCsvFolder(simId: string): Promise<void> {
  const { data: files } = await supabase.storage.from(SIM_CSV_BUCKET).list(simId);
  if (!files || files.length === 0) return;
  const paths = files.map(f => `${simId}/${f.name}`);
  await supabase.storage.from(SIM_CSV_BUCKET).remove(paths);
}

async function copySimCsvFolder(srcSimId: string, dstSimId: string): Promise<void> {
  const { data: files } = await supabase.storage.from(SIM_CSV_BUCKET).list(srcSimId);
  if (!files || files.length === 0) return;
  for (const file of files) {
    const srcPath = `${srcSimId}/${file.name}`;
    const dstPath = `${dstSimId}/${file.name}`;
    const { data: blob, error: dlErr } = await supabase.storage.from(SIM_CSV_BUCKET).download(srcPath);
    if (dlErr || !blob) continue;
    await supabase.storage.from(SIM_CSV_BUCKET).upload(dstPath, blob, { upsert: true });
  }
}

export function SimulatorModal({ isOpen, onClose, guestMode, isSuperAdmin, guestCountdown }: Props) {
  /* ── Simulation tab ── */
  const [simulations, setSimulations] = useState<SimulationRecord[]>([]);
  const [loadingSimulations, setLoadingSimulations] = useState(false);
  const [simConsumerProfiles, setSimConsumerProfiles] = useState<{ value: string; label: string }[]>([]);
  const [simProducerProfiles, setSimProducerProfiles] = useState<{ value: string; label: string }[]>([]);
  const [loadingProfiles, setLoadingProfiles] = useState(false);
  const [simView, setSimView] = useState<'list' | 'create'>('list');
  const [simWizardStep, setSimWizardStep] = useState<1 | 2>(1);
  const [simName, setSimName] = useState('');
  const [simRegion, setSimRegion] = useState<'bruxelles' | 'wallonie' | ''>('');
  const [simSharingType, setSimSharingType] = useState<'A' | 'B' | 'C' | 'D' | ''>('');
  const [simSharingScope, setSimSharingScope] = useState<'batiment' | 'communaute' | ''>('');
  const [simParticipantCount, setSimParticipantCount] = useState(1);
  const [simParticipants, setSimParticipants] = useState<SimParticipant[]>([]);
  const [savingSim, setSavingSim] = useState(false);
  const [editingSimId, setEditingSimId] = useState<string | null>(null);
  const [deletingSimId, setDeletingSimId] = useState<string | null>(null);
  const [runningSim, setRunningSim] = useState(false);
  const [simResult, setSimResult] = useState<(SimResultData & { simId?: string; simName?: string }) | null>(null);
  const [simQHData, setSimQHData] = useState<SimQHParticipant[] | null>(null);
  const [showSimChart, setShowSimChart] = useState(false);
  const [viewingSimResult, setViewingSimResult] = useState<(SimResultData & { simName: string }) | null>(null);

  const [sharedSimulationIds, setSharedSimulationIds] = useState<Set<string>>(new Set());
  const [showShareSimulatorModal, setShowShareSimulatorModal] = useState(false);

  /* ── QH paste modal ── */
  const [pasteModal, setPasteModal] = useState<{
    participantIdx: number;
    kind: 'conso' | 'injection';
    source: 'wizard' | 'pm';
  } | null>(null);
  const [pasteText, setPasteText] = useState('');
  const [pasteError, setPasteError] = useState('');

  /* ── Participant manager ── */
  const [participantManagerSim, setParticipantManagerSim] = useState<SimulationRecord | null>(null);
  const [pmParticipants, setPmParticipants] = useState<SimParticipant[]>([]);
  const [savingPm, setSavingPm] = useState(false);

  const openParticipantManager = async (sim: SimulationRecord) => {
    const pts: SimParticipant[] = (sim.participants || []).map(p => ({
      ...p,
      producerProfileType: p.producerProfileType || '' as SimProducerProfileValue,
      consumptionFile: null,
      injectionFile: null,
      supplierBuyRate: p.supplierBuyRate ?? (p.type === 'producer' ? '25' : '160'),
      communityRate: p.communityRate ?? (p.type === 'producer' ? '65' : '95'),
      supplierSellRate: p.supplierSellRate ?? '25',
      communityBuyRate: p.communityBuyRate ?? '65',
      prosumerInstalledPower: p.prosumerInstalledPower || '',
      prosumerSiteConsumption: p.prosumerSiteConsumption || '',
      prosumerConsumptionProfileType: p.prosumerConsumptionProfileType || '' as SimProfileValue,
    }));
    // Restore CSV files from storage
    const restored = await Promise.all(pts.map(async p => {
      let consumptionFile = p.consumptionFile;
      let injectionFile = p.injectionFile;
      if (p.consumptionFileName && !consumptionFile) {
        try {
          consumptionFile = await downloadSimCsvAsFile(
            simCsvPath(sim.id, p.id, 'conso', p.consumptionFileName), p.consumptionFileName
          );
        } catch { /* file may not exist yet */ }
      }
      if (p.injectionFileName && !injectionFile) {
        try {
          injectionFile = await downloadSimCsvAsFile(
            simCsvPath(sim.id, p.id, 'injection', p.injectionFileName), p.injectionFileName
          );
        } catch { /* file may not exist yet */ }
      }
      return { ...p, consumptionFile, injectionFile };
    }));
    setPmParticipants(restored);
    setParticipantManagerSim(sim);
  };

  const updatePmParticipant = (idx: number, patch: Partial<SimParticipant>) => {
    setPmParticipants(prev => prev.map((p, i) => i === idx ? { ...p, ...patch } : p));
  };

  const removePmParticipant = (idx: number) => {
    setPmParticipants(prev => prev.filter((_, i) => i !== idx));
  };

  const addPmParticipant = () => {
    setPmParticipants(prev => [...prev, makeDefaultParticipant(prev.length)]);
  };

  const savePmParticipants = async (andRun = false) => {
    if (!participantManagerSim) return;
    if (pmParticipants.length === 0) { toast.error('La simulation doit avoir au moins un participant'); return; }
    setSavingPm(true);
    try {
      const updatedParticipants = await uploadParticipantCsvFiles(participantManagerSim.id, pmParticipants);
      const participantsData = updatedParticipants.map(p => ({
        id: p.id, label: p.label, type: p.type, profileType: p.profileType,
        producerProfileType: p.producerProfileType, annualConsumption: p.annualConsumption,
        annualInjection: p.annualInjection, consumptionFileName: p.consumptionFileName,
        injectionFileName: p.injectionFileName, supplierBuyRate: p.supplierBuyRate,
        communityRate: p.communityRate, supplierSellRate: p.supplierSellRate,
        communityBuyRate: p.communityBuyRate, address: p.address || '',
        prosumerInstalledPower: p.prosumerInstalledPower || '',
        prosumerSiteConsumption: p.prosumerSiteConsumption || '',
        prosumerConsumptionProfileType: p.prosumerConsumptionProfileType || '',
        consumptionQhValues: p.consumptionQhValues || null,
        injectionQhValues: p.injectionQhValues || null,
      }));
      const { data, error } = await supabase
        .from('simulations')
        .update({ participants: participantsData, status: andRun ? 'complete' : 'draft' })
        .eq('id', participantManagerSim.id)
        .select()
        .single();
      if (error) throw error;
      setSimulations(prev => prev.map(s => s.id === participantManagerSim.id ? data : s));

      if (andRun) {
        const sim = participantManagerSim;
        setParticipantManagerSim(null);
        setRunningSim(true);
        setSimResult(null);
        try {
          await runSimulationEngine(
            updatedParticipants,
            sim.region || '',
            sim.sharing_type || '',
            sim.sharing_scope || '',
            sim.id,
            sim.name,
            true,
          );
        } catch (err: unknown) {
          const msg = err instanceof Error ? err.message : 'Erreur inconnue';
          toast.error(`Erreur simulation : ${msg}`);
        } finally {
          setRunningSim(false);
        }
      } else {
        setParticipantManagerSim(null);
        toast.success('Participants mis à jour');
      }
    } catch {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSavingPm(false);
    }
  };

  /* ── Quick-edit modal ── */
  const [quickEditSim, setQuickEditSim] = useState<SimulationRecord | null>(null);
  const [quickEditName, setQuickEditName] = useState('');
  const [quickEditRegion, setQuickEditRegion] = useState<'bruxelles' | 'wallonie' | ''>('');
  const [quickEditSharingType, setQuickEditSharingType] = useState<'A' | 'B' | 'C' | 'D' | ''>('');
  const [quickEditSharingScope, setQuickEditSharingScope] = useState<'batiment' | 'communaute' | ''>('');
  const [quickEditParticipantCount, setQuickEditParticipantCount] = useState(1);
  const [savingQuickEdit, setSavingQuickEdit] = useState(false);


  useEffect(() => {
    if (!isOpen) return;
    loadSimulations();
    loadSheetProfiles();
  }, [isOpen]);

  const loadSimulations = async () => {
    setLoadingSimulations(true);
    try {
      const { data } = await supabase.from('simulations').select('*').order('created_at', { ascending: false });
      setSimulations(data || []);

      if (guestMode) {
        const { data: { user } } = await supabase.auth.getUser();
        if (user?.email) {
          const { data: shares } = await supabase
            .from('simulation_shares')
            .select('simulation_id')
            .eq('guest_email', user.email);
          setSharedSimulationIds(new Set((shares || []).map(s => s.simulation_id)));
        }
      }
    } catch { } finally { setLoadingSimulations(false); }
  };

  const parseCsvHeaders = (csvText: string): string[] => {
    const result = Papa.parse<string[]>(csvText, { header: false, preview: 1, skipEmptyLines: true });
    const row = result.data[0] as string[] | undefined;
    return (row || []).map(h => String(h).trim()).filter(Boolean);
  };

  const loadSheetProfiles = async () => {
    setLoadingProfiles(true);
    try {
      const [consRes, prodRes] = await Promise.allSettled([
        fetch(CONSUMER_SHEET_URL),
        fetch(PRODUCER_SHEET_URL),
      ]);

      if (consRes.status === 'fulfilled' && consRes.value.ok) {
        const text = await consRes.value.text();
        const headers = parseCsvHeaders(text);
        setSimConsumerProfiles(headers.map(h => ({ value: h, label: h })));
      } else {
        toast.error('Impossible de charger les profils consommateurs');
      }

      if (prodRes.status === 'fulfilled' && prodRes.value.ok) {
        const text = await prodRes.value.text();
        const headers = parseCsvHeaders(text);
        setSimProducerProfiles(headers.map(h => ({ value: h, label: h })));
      } else {
        toast.error('Impossible de charger les profils producteurs');
      }
    } catch (e) {
      toast.error('Erreur lors du chargement des profils');
    } finally { setLoadingProfiles(false); }
  };

  const makeDefaultParticipant = (index: number): SimParticipant => ({
    id: `p-${Date.now()}-${index}`,
    label: `Participant ${index + 1}`,
    type: 'consumer',
    profileType: '' as SimProfileValue,
    producerProfileType: '' as SimProducerProfileValue,
    annualConsumption: '',
    annualInjection: '',
    consumptionFile: null,
    injectionFile: null,
    consumptionFileName: '',
    injectionFileName: '',
    consumptionQhValues: null,
    injectionQhValues: null,
    supplierBuyRate: '160',
    communityRate: '95',
    supplierSellRate: '25',
    communityBuyRate: '65',
    address: '',
    prosumerInstalledPower: '',
    prosumerSiteConsumption: '',
    prosumerConsumptionProfileType: '' as SimProfileValue,
  });

  const resetSimWizard = () => {
    setSimView('list');
    setSimWizardStep(1);
    setSimName('');
    setSimRegion('');
    setSimSharingType('');
    setSimSharingScope('');
    setSimParticipantCount(1);
    setSimParticipants([]);
    setEditingSimId(null);
    setSimResult(null);
  };

  const handleEditSimulation = async (sim: SimulationRecord) => {
    const pts: SimParticipant[] = (sim.participants || []).map(p => ({
      ...p,
      producerProfileType: p.producerProfileType || '' as SimProducerProfileValue,
      consumptionFile: null,
      injectionFile: null,
      supplierBuyRate: p.supplierBuyRate ?? (p.type === 'producer' ? '25' : '160'),
      communityRate: p.communityRate ?? (p.type === 'producer' ? '65' : '95'),
      supplierSellRate: p.supplierSellRate ?? '25',
      communityBuyRate: p.communityBuyRate ?? '65',
      prosumerInstalledPower: p.prosumerInstalledPower || '',
      prosumerSiteConsumption: p.prosumerSiteConsumption || '',
      prosumerConsumptionProfileType: p.prosumerConsumptionProfileType || '' as SimProfileValue,
    }));
    // Restore CSV files from storage
    const restored = await Promise.all(pts.map(async p => {
      let consumptionFile = p.consumptionFile;
      let injectionFile = p.injectionFile;
      if (p.consumptionFileName && !consumptionFile) {
        try {
          consumptionFile = await downloadSimCsvAsFile(
            simCsvPath(sim.id, p.id, 'conso', p.consumptionFileName), p.consumptionFileName
          );
        } catch { /* file may not exist yet */ }
      }
      if (p.injectionFileName && !injectionFile) {
        try {
          injectionFile = await downloadSimCsvAsFile(
            simCsvPath(sim.id, p.id, 'injection', p.injectionFileName), p.injectionFileName
          );
        } catch { /* file may not exist yet */ }
      }
      return { ...p, consumptionFile, injectionFile };
    }));
    setEditingSimId(sim.id);
    setSimName(sim.name);
    setSimRegion(sim.region || '');
    setSimSharingType(sim.sharing_type || '');
    setSimSharingScope(sim.sharing_scope || '');
    setSimParticipantCount(restored.length || 1);
    setSimParticipants(restored);
    setSimWizardStep(restored.length > 0 ? 2 : 1);
    setSimView('create');
  };

  const handleDeleteSimulation = async (simId: string) => {
    setDeletingSimId(simId);
    try {
      const { error } = await supabase.from('simulations').delete().eq('id', simId);
      if (error) throw error;
      setSimulations(prev => prev.filter(s => s.id !== simId));
      deleteSimCsvFolder(simId);
      toast.success('Simulation supprimée');
    } catch { toast.error('Erreur lors de la suppression'); }
    finally { setDeletingSimId(null); }
  };

  const handleSimStep1Next = () => {
    if (!simName.trim()) { toast.error('Veuillez donner un nom à cette simulation'); return; }
    if (simParticipantCount < 1) { toast.error('Au moins 1 participant requis'); return; }
    const current = simParticipants;
    const newList: SimParticipant[] = Array.from({ length: simParticipantCount }, (_, i) =>
      current[i] ?? makeDefaultParticipant(i)
    );
    setSimParticipants(newList);
    setSimWizardStep(2);
  };

  const updateSimParticipant = (idx: number, patch: Partial<SimParticipant>) => {
    setSimParticipants(prev => prev.map((p, i) => i === idx ? { ...p, ...patch } : p));
  };

  useEffect(() => {
    if (simProducerProfiles.length === 0) return;
    const injectionProfile = simProducerProfiles.find(sp => sp.label.toLowerCase().includes('injection'));
    if (!injectionProfile) return;
    setSimParticipants(prev => prev.map(p => {
      if (p.type === 'prosumer' || p.type === 'consumer_prosumer') {
        return { ...p, producerProfileType: injectionProfile.value as SimProducerProfileValue };
      }
      return p;
    }));
  }, [simProducerProfiles]);

  const uploadParticipantCsvFiles = async (simId: string, participants: SimParticipant[]): Promise<SimParticipant[]> => {
    return Promise.all(participants.map(async p => {
      let consumptionFileName = p.consumptionFileName;
      let injectionFileName = p.injectionFileName;
      if (p.consumptionFile) {
        await uploadSimCsv(simId, p.id, 'conso', p.consumptionFile);
        consumptionFileName = p.consumptionFile.name;
      }
      if (p.injectionFile) {
        await uploadSimCsv(simId, p.id, 'injection', p.injectionFile);
        injectionFileName = p.injectionFile.name;
      }
      return { ...p, consumptionFileName, injectionFileName };
    }));
  };

  const serializeParticipants = (participants: SimParticipant[], getDefaultSupplierRate: (t: SimParticipant['type']) => string, getDefaultCommunityRate: (t: SimParticipant['type']) => string) =>
    participants.map(p => ({
      id: p.id, label: p.label, type: p.type, profileType: p.profileType,
      producerProfileType: p.producerProfileType, annualConsumption: p.annualConsumption,
      annualInjection: p.annualInjection, consumptionFileName: p.consumptionFileName,
      injectionFileName: p.injectionFileName,
      supplierBuyRate: p.supplierBuyRate || getDefaultSupplierRate(p.type),
      communityRate: p.communityRate || getDefaultCommunityRate(p.type),
      supplierSellRate: p.supplierSellRate || '25', communityBuyRate: p.communityBuyRate || '65',
      address: p.address || '', prosumerInstalledPower: p.prosumerInstalledPower || '',
      prosumerSiteConsumption: p.prosumerSiteConsumption || '',
      prosumerConsumptionProfileType: p.prosumerConsumptionProfileType || '',
      consumptionQhValues: p.consumptionQhValues || null,
      injectionQhValues: p.injectionQhValues || null,
    }));

  const persistSimulation = async (resultData: SimResultData | null, showSuccessToast = false) => {
    const { data: { user } } = await supabase.auth.getUser();
    const getDefaultSupplierRate = (type: SimParticipant['type']) => type === 'producer' ? '25' : '160';
    const getDefaultCommunityRate = (type: SimParticipant['type']) => type === 'producer' ? '65' : '95';

    const savedResults = resultData
      ? { totalShared: resultData.totalShared, totalConsumption: resultData.totalConsumption, totalProduction: resultData.totalProduction, totalGrossConsumption: resultData.totalGrossConsumption, totalGrossProduction: resultData.totalGrossProduction, totalAutoConsumption: resultData.totalAutoConsumption, totalGains: resultData.totalGains, totalCommodityGains: resultData.totalCommodityGains, totalNetworkGains: resultData.totalNetworkGains, networkReductionRate: resultData.networkReductionRate, hasGains: resultData.hasGains, monthlyData: resultData.monthlyData, participantMonthlyData: resultData.participantMonthlyData }
      : {};
    const savedStatus = resultData ? 'complete' : 'draft';

    if (editingSimId) {
      const updatedParticipants = await uploadParticipantCsvFiles(editingSimId, simParticipants);
      const participantsData = serializeParticipants(updatedParticipants, getDefaultSupplierRate, getDefaultCommunityRate);
      const { data, error } = await supabase.from('simulations').update({
        name: simName.trim(), participants: participantsData, results: savedResults,
        status: savedStatus, region: simRegion || null,
        sharing_type: simSharingType || null, sharing_scope: simSharingScope || null,
      }).eq('id', editingSimId).select().single();
      if (error) throw error;
      setSimulations(prev => prev.map(s => s.id === editingSimId ? data : s));
      if (showSuccessToast) toast.success('Simulation mise à jour');
    } else {
      const { data: newSim, error: insertError } = await supabase.from('simulations').insert({
        name: simName.trim(), role: 'consumer', inputs: {}, results: savedResults,
        reduction_rate: 0.15, participants: [], status: savedStatus,
        created_by: user?.id, created_by_email: user?.email ?? null,
        region: simRegion || null, sharing_type: simSharingType || null, sharing_scope: simSharingScope || null,
      }).select().single();
      if (insertError) throw insertError;
      const updatedParticipants = await uploadParticipantCsvFiles(newSim.id, simParticipants);
      const participantsData = serializeParticipants(updatedParticipants, getDefaultSupplierRate, getDefaultCommunityRate);
      const { data, error } = await supabase.from('simulations').update({ participants: participantsData })
        .eq('id', newSim.id).select().single();
      if (error) throw error;
      setSimulations(prev => [data, ...prev]);
      setEditingSimId(newSim.id);
      if (showSuccessToast) toast.success('Simulation enregistrée');
    }
  };

  const handleSaveSimulation = async () => {
    setSavingSim(true);
    try {
      await persistSimulation(simResult, true);
      resetSimWizard();
    } catch { toast.error('Erreur lors de la sauvegarde'); }
    finally { setSavingSim(false); }
  };

  const handleDuplicateSimulation = async (sim: SimulationRecord) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { data, error } = await supabase.from('simulations').insert({
        name: `${sim.name} (copie)`,
        role: sim.role,
        inputs: sim.inputs,
        results: sim.results,
        reduction_rate: sim.reduction_rate,
        participants: sim.participants || [],
        status: sim.status || 'draft',
        created_by: user?.id,
        created_by_email: user?.email ?? null,
      }).select().single();
      if (error) throw error;
      setSimulations(prev => [data, ...prev]);
      copySimCsvFolder(sim.id, data.id);
      toast.success('Simulation dupliquée');
    } catch { toast.error('Erreur lors de la duplication'); }
  };

  const openQuickEdit = (sim: SimulationRecord) => {
    setQuickEditSim(sim);
    setQuickEditName(sim.name);
    setQuickEditRegion(sim.region || '');
    setQuickEditSharingType(sim.sharing_type || '');
    setQuickEditSharingScope(sim.sharing_scope || '');
    setQuickEditParticipantCount((sim.participants || []).length || 1);
  };

  const handleQuickEditSave = async () => {
    if (!quickEditSim) return;
    if (!quickEditName.trim()) { toast.error('Le nom ne peut pas être vide'); return; }
    setSavingQuickEdit(true);
    try {
      const { data, error } = await supabase
        .from('simulations')
        .update({
          name: quickEditName.trim(),
          region: quickEditRegion || null,
          sharing_type: quickEditSharingType || null,
          sharing_scope: quickEditSharingScope || null,
        })
        .eq('id', quickEditSim.id)
        .select()
        .single();
      if (error) throw error;
      setSimulations(prev => prev.map(s => s.id === quickEditSim.id ? data : s));
      const savedSim = quickEditSim;
      setQuickEditSim(null);
      openParticipantManager(data ?? savedSim);
    } catch { toast.error('Erreur lors de la sauvegarde'); }
    finally { setSavingQuickEdit(false); }
  };

  const handlePasteConfirm = () => {
    setPasteError('');
    if (!pasteModal) return;
    const raw = pasteText.trim();
    if (!raw) { setPasteError('Aucune donnée collée.'); return; }

    // Split on single newlines — empty lines (multiple consecutive newlines) produce a 0
    const lines = raw.split(/\r?\n/);
    const values: number[] = [];
    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed) {
        // Blank line between two values = missing value → 0
        values.push(0);
        continue;
      }
      // Split on tab or semicolon only — commas may be decimal separators (FR/NL locale)
      const cols = trimmed.split(/[\t;]+/);
      // If multiple columns, prefer the second (index 1) as it's likely the value column;
      // otherwise fall back to the first. Replace comma with dot for decimal parsing.
      const raw = (cols.length >= 2 ? cols[1] : cols[0]).trim().replace(',', '.');
      const num = parseFloat(raw);
      values.push(isNaN(num) ? 0 : num);
    }

    if (values.length === 0) { setPasteError('Impossible de lire les valeurs. Vérifiez le format.'); return; }

    // Pad or truncate to 35040
    const NUM_QH = 35040;
    const padded = Array.from({ length: NUM_QH }, (_, i) => values[i] ?? 0);

    // Build a simple single-column CSV (no datetime) to avoid PapaParse delimiter auto-detection issues
    const rows = ['valeur_mwh'];
    for (let i = 0; i < NUM_QH; i++) {
      rows.push(String(padded[i]));
    }
    const csvContent = rows.join('\n');
    const file = new File([csvContent], `paste_${pasteModal.kind}_${pasteModal.participantIdx}.csv`, { type: 'text/csv' });

    const patch = pasteModal.kind === 'conso'
      ? { consumptionFile: file, consumptionFileName: file.name, annualConsumption: '', consumptionQhValues: padded }
      : { injectionFile: file, injectionFileName: file.name, annualInjection: '', injectionQhValues: padded };

    if (pasteModal.source === 'wizard') {
      updateSimParticipant(pasteModal.participantIdx, patch);
    } else {
      updatePmParticipant(pasteModal.participantIdx, patch);
    }

    setPasteModal(null);
    setPasteText('');
    setPasteError('');
  };

  const downloadQhTemplate = () => {
    const rows: string[] = ['datetime,valeur_mwh'];
    const start = new Date(2024, 0, 1, 0, 0, 0);
    const pad = (n: number) => String(n).padStart(2, '0');
    for (let i = 0; i < 35040; i++) {
      const d = new Date(start.getTime() + i * 15 * 60 * 1000);
      const dt = `${pad(d.getDate())}/${pad(d.getMonth() + 1)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
      rows.push(`${dt},0`);
    }
    const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'template_profil_quart_horaire.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const parseUploadedProfile = async (file: File): Promise<number[]> => {
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (ext === 'xlsx' || ext === 'xls') {
      const XLSX = await import('xlsx');
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows: unknown[][] = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: 0 });
      if (rows.length < 2) throw new Error('Fichier vide ou invalide');
      const values: number[] = [];
      for (let i = 1; i < rows.length; i++) {
        const row = rows[i];
        if (!row || row.length < 2) continue;
        const raw = String(row[1] ?? '').trim().replace(',', '.');
        values.push(parseFloat(raw) || 0);
      }
      return values;
    }
    return new Promise((resolve, reject) => {
      Papa.parse<string[]>(file, {
        skipEmptyLines: true,
        delimiter: ',',
        complete: (result) => {
          const rows = result.data;
          if (rows.length < 2) { reject(new Error('Fichier vide ou invalide')); return; }
          const values: number[] = [];
          for (let i = 1; i < rows.length; i++) {
            const row = rows[i];
            if (!row || row.length === 0) continue;
            // Single-column CSV (pasted data): value in col 0; two-column CSV (datetime,value): value in col 1
            const raw = String(row.length >= 2 ? row[1] : row[0]).trim().replace(',', '.');
            values.push(parseFloat(raw) || 0);
          }
          resolve(values);
        },
        error: (err: Error) => reject(err),
      });
    });
  };

  const fetchProfileData = async (sheetUrl: string, columnName: string): Promise<number[]> => {
    const res = await fetch(sheetUrl);
    if (!res.ok) throw new Error(`Impossible de charger le sheet`);
    const text = await res.text();
    const result = Papa.parse<Record<string, string>>(text, {
      header: true,
      skipEmptyLines: true,
      dynamicTyping: false,
    });
    if (!result.data || result.data.length === 0) throw new Error(`Aucune donnée dans le sheet`);
    const headers = result.meta.fields || [];
    if (!headers.includes(columnName)) {
      throw new Error(`Colonne "${columnName}" introuvable. Colonnes disponibles: ${headers.slice(0, 5).join(', ')}`);
    }
    return result.data.map(row => {
      const raw = (row[columnName] || '').trim().replace(',', '.');
      return parseFloat(raw) || 0;
    });
  };

  const runSimulationEngine = async (
    participants: SimParticipant[],
    region: string,
    sharingType: string,
    sharingScope: string,
    saveToSimId: string | null,
    label: string,
    openResultModal = false,
  ): Promise<SimResultData | null> => {
    const NUM_QH = 35040;

    const consumptionProfiles: number[][] = [];
    const productionProfiles: number[][] = [];
    const participantNetAnnual: Record<string, number> = {};
    const participantSurplusAnnual: Record<string, number> = {};
    const participantGrossConsumption: Record<string, number> = {};
    const participantGrossProduction: Record<string, number> = {};
    const participantAutoConsumption: Record<string, number> = {};

    for (const p of participants) {
      if (p.type === 'consumer' || p.type === 'mixed') {
        if (p.consumptionQhValues && p.consumptionQhValues.length > 0) {
          // Use raw QH values stored directly (pasted data) — no CSV round-trip
          const rawData = p.consumptionQhValues;
          const total = rawData.reduce((a, b) => a + b, 0);
          const paddedData = Array.from({ length: NUM_QH }, (_, i) => rawData[i] ?? 0);
          participantGrossConsumption[p.id] = total;
          consumptionProfiles.push(paddedData);
        } else if (p.consumptionFile) {
          const rawData = await parseUploadedProfile(p.consumptionFile);
          if (rawData.length === 0) { toast.error(`Fichier vide pour ${p.label}`); return null; }
          const total = rawData.reduce((a, b) => a + b, 0);
          const normalized = total > 0 ? rawData.map(v => v / total) : rawData;
          const annualVol = parseFloat(p.annualConsumption) || total;
          const scaledData = normalized.map(v => v * annualVol);
          const paddedData = Array.from({ length: NUM_QH }, (_, i) => scaledData[i] ?? 0);
          participantGrossConsumption[p.id] = annualVol;
          consumptionProfiles.push(paddedData);
        } else {
          if (!p.profileType) { toast.error(`Profil consommation manquant pour ${p.label}`); return null; }
          if (!p.annualConsumption || isNaN(parseFloat(p.annualConsumption))) { toast.error(`Consommation annuelle manquante pour ${p.label}`); return null; }
          const normalizedData = await fetchProfileData(CONSUMER_SHEET_URL, p.profileType);
          if (normalizedData.length === 0) { toast.error(`Données vides pour le profil "${p.profileType}"`); return null; }
          const annualVol = parseFloat(p.annualConsumption);
          const scaledData = normalizedData.map(v => v * annualVol);
          const paddedData = Array.from({ length: NUM_QH }, (_, i) => scaledData[i] ?? 0);
          participantGrossConsumption[p.id] = annualVol;
          consumptionProfiles.push(paddedData);
        }
      }
      if (p.type === 'prosumer') {
        if (!p.producerProfileType) { toast.error(`Profil PV manquant pour ${p.label}`); return null; }
        if (!p.prosumerInstalledPower || isNaN(parseFloat(p.prosumerInstalledPower))) { toast.error(`Puissance installée manquante pour ${p.label}`); return null; }
        if (!p.prosumerConsumptionProfileType) { toast.error(`Profil de consommation sur site manquant pour ${p.label}`); return null; }
        if (!p.prosumerSiteConsumption || isNaN(parseFloat(p.prosumerSiteConsumption))) { toast.error(`Consommation annuelle sur site manquante pour ${p.label}`); return null; }
        const pvNormalized = await fetchProfileData(PRODUCER_SHEET_URL, p.producerProfileType);
        if (pvNormalized.length === 0) { toast.error(`Données PV vides pour le profil "${p.producerProfileType}"`); return null; }
        const consNormalized = await fetchProfileData(CONSUMER_SHEET_URL, p.prosumerConsumptionProfileType);
        if (consNormalized.length === 0) { toast.error(`Données conso vides pour le profil "${p.prosumerConsumptionProfileType}"`); return null; }
        const installedPower = parseFloat(p.prosumerInstalledPower);
        const siteConsumption = parseFloat(p.prosumerSiteConsumption);
        const pvProfile = Array.from({ length: NUM_QH }, (_, i) => (pvNormalized[i] ?? 0) * installedPower * 0.95);
        const consProfile = Array.from({ length: NUM_QH }, (_, i) => (consNormalized[i] ?? 0) * siteConsumption);
        const netInjection = Array.from({ length: NUM_QH }, (_, i) => Math.max(0, pvProfile[i] - consProfile[i]));
        const autoConsQH = Array.from({ length: NUM_QH }, (_, i) => Math.min(pvProfile[i], consProfile[i]));
        participantNetAnnual[p.id] = netInjection.reduce((a, b) => a + b, 0);
        participantGrossProduction[p.id] = pvProfile.reduce((a, b) => a + b, 0);
        participantGrossConsumption[p.id] = consProfile.reduce((a, b) => a + b, 0);
        participantAutoConsumption[p.id] = autoConsQH.reduce((a, b) => a + b, 0);
        productionProfiles.push(netInjection);
      }
      if (p.type === 'consumer_prosumer') {
        if (!p.profileType) { toast.error(`Profil de consommation manquant pour ${p.label}`); return null; }
        if (!p.annualConsumption || isNaN(parseFloat(p.annualConsumption))) { toast.error(`Consommation annuelle manquante pour ${p.label}`); return null; }
        if (!p.producerProfileType) { toast.error(`Profil PV manquant pour ${p.label}`); return null; }
        if (!p.prosumerInstalledPower || isNaN(parseFloat(p.prosumerInstalledPower))) { toast.error(`Puissance installée manquante pour ${p.label}`); return null; }
        const consNormalized = await fetchProfileData(CONSUMER_SHEET_URL, p.profileType);
        if (consNormalized.length === 0) { toast.error(`Données conso vides pour le profil "${p.profileType}"`); return null; }
        const pvNormalized = await fetchProfileData(PRODUCER_SHEET_URL, p.producerProfileType);
        if (pvNormalized.length === 0) { toast.error(`Données PV vides pour le profil "${p.producerProfileType}"`); return null; }
        const siteConsumption = parseFloat(p.annualConsumption);
        const installedPower = parseFloat(p.prosumerInstalledPower);
        const consProfile = Array.from({ length: NUM_QH }, (_, i) => (consNormalized[i] ?? 0) * siteConsumption);
        const pvProfile = Array.from({ length: NUM_QH }, (_, i) => (pvNormalized[i] ?? 0) * installedPower * 0.95);
        const netConsumption = Array.from({ length: NUM_QH }, (_, i) => Math.max(0, consProfile[i] - pvProfile[i]));
        const surplusProduction = Array.from({ length: NUM_QH }, (_, i) => Math.max(0, pvProfile[i] - consProfile[i]));
        const autoConsQH = Array.from({ length: NUM_QH }, (_, i) => Math.min(pvProfile[i], consProfile[i]));
        participantNetAnnual[p.id] = netConsumption.reduce((a, b) => a + b, 0);
        participantSurplusAnnual[p.id] = surplusProduction.reduce((a, b) => a + b, 0);
        participantGrossConsumption[p.id] = consProfile.reduce((a, b) => a + b, 0);
        participantGrossProduction[p.id] = pvProfile.reduce((a, b) => a + b, 0);
        participantAutoConsumption[p.id] = autoConsQH.reduce((a, b) => a + b, 0);
        consumptionProfiles.push(netConsumption);
        productionProfiles.push(surplusProduction);
      }
      if (p.type === 'producer' || p.type === 'mixed') {
        if (p.injectionQhValues && p.injectionQhValues.length > 0) {
          // Use raw QH values stored directly (pasted data) — no CSV round-trip
          const rawData = p.injectionQhValues;
          const total = rawData.reduce((a, b) => a + b, 0);
          const paddedData = Array.from({ length: NUM_QH }, (_, i) => rawData[i] ?? 0);
          participantGrossProduction[p.id] = total;
          productionProfiles.push(paddedData);
        } else if (p.injectionFile) {
          const rawDataInj = await parseUploadedProfile(p.injectionFile);
          if (rawDataInj.length === 0) { toast.error(`Fichier vide pour ${p.label}`); return null; }
          const total = rawDataInj.reduce((a, b) => a + b, 0);
          const normalized = total > 0 ? rawDataInj.map(v => v / total) : rawDataInj;
          const annualVol = parseFloat(p.annualInjection) || total;
          const scaledData = normalized.map(v => v * annualVol);
          const paddedData = Array.from({ length: NUM_QH }, (_, i) => scaledData[i] ?? 0);
          participantGrossProduction[p.id] = annualVol;
          productionProfiles.push(paddedData);
        } else {
          if (!p.producerProfileType) { toast.error(`Profil production manquant pour ${p.label}`); return null; }
          if (!p.annualInjection || isNaN(parseFloat(p.annualInjection))) { toast.error(`Injection annuelle manquante pour ${p.label}`); return null; }
          const normalizedData = await fetchProfileData(PRODUCER_SHEET_URL, p.producerProfileType);
          if (normalizedData.length === 0) { toast.error(`Données vides pour le profil "${p.producerProfileType}"`); return null; }
          const annualVol = parseFloat(p.annualInjection);
          const scaledData = normalizedData.map(v => v * annualVol);
          const paddedData = Array.from({ length: NUM_QH }, (_, i) => scaledData[i] ?? 0);
          participantGrossProduction[p.id] = annualVol;
          productionProfiles.push(paddedData);
        }
      }
    }

    if (consumptionProfiles.length === 0) { toast.error('Aucun profil de consommation'); return null; }
    if (productionProfiles.length === 0) { toast.error('Aucun profil de production'); return null; }

    const MONTH_LABELS = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
    const DAYS_PER_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    const QH_PER_DAY = 96;

    const monthly = MONTH_LABELS.map(m => ({ month: m, consumption: 0, production: 0, shared: 0, gains: 0, commodityGains: 0, networkGains: 0 }));
    let monthIdx = 0;
    let dayInMonth = 0;
    let qhInDay = 0;

    const networkReductionRate = getNetworkReductionRate(region, sharingType, sharingScope);

    const consumerParticipants: { p: SimParticipant; gainRate: number }[] = [];
    const producerParticipants: { p: SimParticipant; gainRate: number }[] = [];

    const pr = (v: string | undefined, def: number) => { const n = parseFloat(v ?? ''); return isNaN(n) ? def : n; };

    for (const p of participants) {
      if (p.type === 'consumer' || p.type === 'mixed' || p.type === 'consumer_prosumer') {
        const supplierBuy = pr(p.supplierBuyRate, 160);
        const communityS = pr(p.communityRate, 95);
        consumerParticipants.push({ p, gainRate: supplierBuy - communityS });
      }
      if (p.type === 'prosumer') {
        const communityB = pr(p.communityBuyRate, 65);
        const supplierSell = pr(p.supplierSellRate, 25);
        producerParticipants.push({ p, gainRate: communityB - supplierSell });
      }
      if (p.type === 'producer' || p.type === 'mixed') {
        const communityB = pr(p.communityBuyRate, 65);
        const supplierSell = pr(p.supplierSellRate, 25);
        producerParticipants.push({ p, gainRate: communityB - supplierSell });
      }
      if (p.type === 'consumer_prosumer') {
        const communityB = pr(p.communityBuyRate, 65);
        const supplierSell = pr(p.supplierSellRate, 25);
        producerParticipants.push({ p, gainRate: communityB - supplierSell });
      }
    }

    const hasGains = consumerParticipants.some(c => c.gainRate !== 0) || producerParticipants.some(c => c.gainRate !== 0) || networkReductionRate > 0;

    const participantMonthlyShared: Record<string, number[]> = {};
    const participantMonthlyBought: Record<string, number[]> = {};
    const participantMonthlySold: Record<string, number[]> = {};
    const participantConsShared: Record<string, number> = {};
    const participantProdShared: Record<string, number> = {};
    for (const p of participants) {
      participantMonthlyShared[p.id] = new Array(12).fill(0);
      participantMonthlyBought[p.id] = new Array(12).fill(0);
      participantMonthlySold[p.id] = new Array(12).fill(0);
      participantConsShared[p.id] = 0;
      participantProdShared[p.id] = 0;
    }

    const participantQHShared: Record<string, number[]> = {};
    const participantQHCons: Record<string, number[]> = {};
    const participantQHProd: Record<string, number[]> = {};
    for (const p of participants) {
      participantQHShared[p.id] = new Array(NUM_QH).fill(0);
      participantQHCons[p.id] = new Array(NUM_QH).fill(0);
      participantQHProd[p.id] = new Array(NUM_QH).fill(0);
    }

    let totalShared = 0;

    for (let i = 0; i < NUM_QH; i++) {
      const totalCons = consumptionProfiles.reduce((acc, profile) => acc + profile[i], 0);
      const totalProd = productionProfiles.reduce((acc, profile) => acc + profile[i], 0);
      const shared = Math.min(totalCons, totalProd);
      totalShared += shared;

      let consGains = 0;
      let tempConsProfileIdx = 0;
      for (const { p, gainRate } of consumerParticipants) {
        const pCons = consumptionProfiles[tempConsProfileIdx]?.[i] ?? 0;
        const pShared = totalCons > 0 ? shared * (pCons / totalCons) : 0;
        participantMonthlyBought[p.id][monthIdx] += pShared;
        participantMonthlyShared[p.id][monthIdx] += pShared;
        participantQHShared[p.id][i] += pShared;
        participantQHCons[p.id][i] = pCons;
        participantConsShared[p.id] += pShared;
        consGains += pShared * gainRate;
        tempConsProfileIdx++;
      }

      let prodGains = 0;
      let tempProdProfileIdx = 0;
      for (const { p, gainRate } of producerParticipants) {
        const pProd = productionProfiles[tempProdProfileIdx]?.[i] ?? 0;
        const pShared = totalProd > 0 ? shared * (pProd / totalProd) : 0;
        participantMonthlySold[p.id][monthIdx] += pShared;
        participantMonthlyShared[p.id][monthIdx] += pShared;
        participantQHShared[p.id][i] += pShared;
        participantQHProd[p.id][i] = pProd;
        participantProdShared[p.id] += pShared;
        prodGains += pShared * gainRate;
        tempProdProfileIdx++;
      }

      monthly[monthIdx].consumption += totalCons;
      monthly[monthIdx].production += totalProd;
      monthly[monthIdx].shared += shared;
      monthly[monthIdx].commodityGains = (monthly[monthIdx].commodityGains || 0) + consGains + prodGains;
      monthly[monthIdx].gains += consGains + prodGains;

      qhInDay++;
      if (qhInDay >= QH_PER_DAY) {
        qhInDay = 0;
        dayInMonth++;
        if (dayInMonth >= DAYS_PER_MONTH[monthIdx]) {
          dayInMonth = 0;
          if (monthIdx < 11) monthIdx++;
        }
      }
    }

    for (const m of monthly) {
      const mNetworkGains = m.shared * networkReductionRate;
      m.networkGains = mNetworkGains;
      m.gains += mNetworkGains;
    }

    const totalGains = monthly.reduce((acc, m) => acc + m.gains, 0);
    const totalCommodityGains = monthly.reduce((acc, m) => acc + (m.commodityGains || 0), 0);
    const totalNetworkGains = monthly.reduce((acc, m) => acc + (m.networkGains || 0), 0);
    const totalConsumption = monthly.reduce((acc, m) => acc + m.consumption, 0);
    const totalProduction = monthly.reduce((acc, m) => acc + m.production, 0);

    const participantMonthlyData = participants.map(p => {
      const monthlyShared = participantMonthlyShared[p.id];
      const pTotalShared = monthlyShared.reduce((a, b) => a + b, 0);
      let pCommodityGains = 0;
      let pNetworkGains = 0;
      if (p.type === 'consumer_prosumer') {
        const supplierBuy = pr(p.supplierBuyRate, 160);
        const communityS = pr(p.communityRate, 95);
        const communityB = pr(p.communityBuyRate, 65);
        const supplierSell = pr(p.supplierSellRate, 25);
        const consShared = participantConsShared[p.id] ?? 0;
        const prodShared = participantProdShared[p.id] ?? 0;
        pCommodityGains = consShared * (supplierBuy - communityS) + prodShared * (communityB - supplierSell);
        pNetworkGains = consShared * networkReductionRate;
      } else {
        let gainRate = 0;
        if (p.type === 'consumer' || p.type === 'mixed') {
          const supplierBuy = pr(p.supplierBuyRate, 160);
          const communityS = pr(p.communityRate, 95);
          gainRate += supplierBuy - communityS;
        }
        if (p.type === 'prosumer') {
          const communityB = pr(p.communityBuyRate, 65);
          const supplierSell = pr(p.supplierSellRate, 25);
          gainRate += communityB - supplierSell;
        }
        if (p.type === 'producer' || p.type === 'mixed') {
          const communityB = pr(p.communityBuyRate, 65);
          const supplierSell = pr(p.supplierSellRate, 25);
          gainRate += communityB - supplierSell;
        }
        const isConsumer = p.type === 'consumer' || p.type === 'mixed';
        pCommodityGains = pTotalShared * gainRate;
        pNetworkGains = isConsumer ? pTotalShared * networkReductionRate : 0;
      }
      const pAutoConsumption = participantAutoConsumption[p.id] ?? 0;
      const pGrossConsumption = participantGrossConsumption[p.id] ?? 0;
      const pGrossProduction = participantGrossProduction[p.id] ?? 0;
      const monthlyBought = participantMonthlyBought[p.id];
      const monthlySold = participantMonthlySold[p.id];
      const pTotalBought = monthlyBought.reduce((a, b) => a + b, 0);
      const pTotalSold = monthlySold.reduce((a, b) => a + b, 0);
      return {
        participantId: p.id, label: p.label, type: p.type,
        monthlyShared, monthlyBought, monthlySold,
        totalShared: pTotalShared, totalBought: pTotalBought, totalSold: pTotalSold,
        gains: pCommodityGains + pNetworkGains, commodityGains: pCommodityGains, networkGains: pNetworkGains,
        annualConsumption: p.type === 'consumer_prosumer' ? (participantNetAnnual[p.id] ?? 0) : p.type === 'prosumer' ? 0 : (participantGrossConsumption[p.id] ?? parseFloat(p.annualConsumption) ?? 0),
        annualProduction: p.type === 'consumer_prosumer' ? (participantSurplusAnnual[p.id] ?? 0) : p.type === 'prosumer' ? (participantNetAnnual[p.id] ?? 0) : p.type === 'consumer' ? 0 : (participantGrossProduction[p.id] ?? parseFloat(p.annualInjection) ?? 0),
        grossConsumption: pGrossConsumption, grossProduction: pGrossProduction, autoConsumption: pAutoConsumption,
        supplierBuyRate: parseFloat(p.supplierBuyRate) || 0,
        communityRate: parseFloat(p.communityRate) || 0,
        address: p.address || '',
      };
    });

    const totalGrossConsumption = participantMonthlyData.reduce((acc, p) => acc + p.grossConsumption, 0);
    const totalGrossProduction = participantMonthlyData.reduce((acc, p) => acc + p.grossProduction, 0);
    const totalAutoConsumption = participantMonthlyData.reduce((acc, p) => acc + p.autoConsumption, 0);

    const resultData: SimResultData = { totalShared, totalConsumption, totalProduction, totalGrossConsumption, totalGrossProduction, totalAutoConsumption, totalGains, totalCommodityGains, totalNetworkGains, networkReductionRate, hasGains, monthlyData: monthly, participantMonthlyData };

    const qhParticipants: SimQHParticipant[] = participants.map(p => ({
      participantId: p.id, label: p.label, type: p.type,
      consumption: participantQHCons[p.id],
      production: participantQHProd[p.id],
      shared: participantQHShared[p.id],
    }));

    if (saveToSimId) {
      await supabase.from('simulations').update({ results: resultData, status: 'complete' }).eq('id', saveToSimId);
      setSimulations(prev => prev.map(s => s.id === saveToSimId ? { ...s, results: resultData, status: 'complete' } : s));
    }

    setSimResult({ ...resultData, simName: label });
    setSimQHData(qhParticipants);
    if (openResultModal) setViewingSimResult({ ...resultData, simName: label });
    toast.success(`Simulation terminée : ${totalShared.toFixed(2)} MWh partagés`);
    return resultData;
  };

  const handleRunSimulation = async () => {
    if (simParticipants.length === 0) { toast.error('Aucun participant configuré'); return; }
    setRunningSim(true);
    setSimResult(null);
    try {
      const result = await runSimulationEngine(simParticipants, simRegion, simSharingType, simSharingScope, editingSimId, simName);
      if (result) {
        // Auto-save with results immediately after calculation
        setSavingSim(true);
        try {
          await persistSimulation(result);
        } catch { toast.error('Erreur lors de la sauvegarde automatique'); }
        finally { setSavingSim(false); }
        setShowSimChart(true);
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Erreur inconnue';
      toast.error(`Erreur simulation : ${msg}`);
    } finally {
      setRunningSim(false);
    }
  };


  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="bg-white w-full h-full flex flex-col overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-100 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-[#F8BD3D] rounded-lg flex items-center justify-center">
              <Calculator className="w-5 h-5 text-neutral-900" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-neutral-900">Simulateur</h2>
              <p className="text-xs text-neutral-500">Créez et gérez vos simulations d'énergie</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-neutral-100 rounded-lg transition-colors">
            <X className="w-5 h-5 text-neutral-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex flex-col">
            <div className="flex-1 overflow-hidden flex flex-col">

              {/* ── LIST VIEW ── */}
              {simView === 'list' && (
                <div className="flex-1 overflow-y-auto p-6">
                  <div className="max-w-3xl mx-auto space-y-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-semibold text-neutral-900">Simulations</h3>
                        <p className="text-sm text-neutral-500">
                          {guestMode && sharedSimulationIds.size > 0
                            ? 'Consultez les simulations partagées avec vous'
                            : 'Créez et gérez vos simulations d\'énergie'}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {guestMode && guestCountdown && (guestCountdown.daysLeft > 0 || guestCountdown.hoursLeft > 0) && (
                          <div className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full ${guestCountdown.daysLeft <= 3 ? 'bg-amber-100 text-amber-700' : 'bg-neutral-100 text-neutral-500'}`}>
                            <Clock className="w-3.5 h-3.5" />
                            {guestCountdown.daysLeft > 0
                              ? <span>{guestCountdown.daysLeft} jour{guestCountdown.daysLeft > 1 ? 's' : ''} restant{guestCountdown.daysLeft > 1 ? 's' : ''}</span>
                              : <span>{guestCountdown.hoursLeft}h restante{guestCountdown.hoursLeft > 1 ? 's' : ''}</span>
                            }
                          </div>
                        )}
                        {!(guestMode && sharedSimulationIds.size > 0) && (
                          <button
                            onClick={() => { setSimView('create'); setSimWizardStep(1); setSimName(''); setSimRegion(''); setSimSharingType(''); setSimSharingScope(''); setSimParticipantCount(1); setSimParticipants([]); }}
                            className="flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm text-neutral-900 transition-all hover:opacity-90 shadow-sm"
                            style={{ backgroundColor: '#F8BD3D' }}
                          >
                            <Plus className="w-4 h-4" />
                            Nouvelle simulation
                          </button>
                        )}
                      </div>
                    </div>

                    {loadingSimulations ? (
                      <div className="flex items-center justify-center py-16">
                        <Loader2 className="w-6 h-6 animate-spin text-neutral-300" />
                      </div>
                    ) : simulations.length === 0 ? (
                      <div className="text-center py-20 border-2 border-dashed border-neutral-200 rounded-2xl">
                        <Calculator className="w-10 h-10 text-neutral-300 mx-auto mb-3" />
                        <p className="text-sm font-medium text-neutral-500">Aucune simulation enregistrée</p>
                        <p className="text-xs text-neutral-400 mt-1">Créez votre première simulation en cliquant sur le bouton ci-dessus</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {simulations.map(sim => {
                          const pts: SimParticipant[] = sim.participants || [];
                          const consumers = pts.filter(p => p.type === 'consumer' || p.type === 'mixed').length;
                          const producers = pts.filter(p => p.type === 'producer' || p.type === 'mixed').length;
                          const prosumers = pts.filter(p => p.type === 'prosumer').length;
                          const consumerProsumers = pts.filter(p => p.type === 'consumer_prosumer').length;
                          const isSharedReadOnly = guestMode && sharedSimulationIds.has(sim.id);
                          return (
                            <div key={sim.id} className="border border-neutral-200 rounded-xl overflow-hidden hover:border-neutral-300 transition-colors bg-white">
                              <div className="flex items-center gap-4 px-4 py-3.5">
                                <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                                  <Calculator className="w-5 h-5 text-amber-600" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-semibold text-neutral-900 truncate mb-0.5">{sim.name}</p>
                                  <div className="flex items-center gap-1.5 flex-wrap">
                                    <span className="text-xs text-neutral-400">
                                      {new Date(sim.created_at).toLocaleDateString('fr-BE', { day: '2-digit', month: 'short', year: 'numeric' })}
                                    </span>
                                    {isSuperAdmin && sim.created_by_email && (
                                      <span className="text-xs text-blue-500 font-medium">• {sim.created_by_email}</span>
                                    )}
                                    <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-neutral-100 text-neutral-600">
                                      {pts.length} participant{pts.length > 1 ? 's' : ''}
                                    </span>
                                    {sim.status === 'complete' ? (
                                      <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Calculée</span>
                                    ) : (
                                      <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-neutral-100 text-neutral-500">Brouillon</span>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                  {isSharedReadOnly && (
                                    <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 flex-shrink-0">Partagée</span>
                                  )}
                                  {sim.status === 'complete' && (sim.results as SimResultData)?.monthlyData && (
                                    <button
                                      onClick={() => setViewingSimResult({ ...(sim.results as SimResultData), simName: sim.name })}
                                      className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-emerald-200 text-xs font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 transition-colors"
                                    >
                                      <BarChart3 className="w-3.5 h-3.5" />
                                      Résultats
                                    </button>
                                  )}
                                  {!isSharedReadOnly && (
                                    <>
                                      <button
                                        onClick={() => openQuickEdit(sim)}
                                        className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-neutral-200 text-xs font-medium text-neutral-600 hover:bg-neutral-50 hover:text-neutral-800 transition-colors"
                                        title="Modifier les informations générales"
                                      >
                                        <Pencil className="w-3.5 h-3.5" />
                                        Modifier
                                      </button>
                                      <button
                                        onClick={() => handleDeleteSimulation(sim.id)}
                                        disabled={deletingSimId === sim.id}
                                        className="p-2 rounded-lg border border-neutral-200 text-neutral-500 hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition-colors disabled:opacity-50"
                                        title="Supprimer"
                                      >
                                        {deletingSimId === sim.id
                                          ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                          : <Trash2 className="w-3.5 h-3.5" />
                                        }
                                      </button>
                                      <button
                                        onClick={() => handleDuplicateSimulation(sim)}
                                        className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-neutral-200 text-xs font-medium text-neutral-600 hover:bg-neutral-50 transition-colors"
                                      >
                                        <Copy className="w-3.5 h-3.5" />
                                        Dupliquer
                                      </button>
                                    </>
                                  )}
                                </div>
                              </div>
                              {pts.length > 0 && (
                                <div className="px-4 py-2 bg-neutral-50 border-t border-neutral-100 flex flex-wrap gap-1.5">
                                  {pts.map((p, i) => (
                                    <span key={i} className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
                                      (p.type === 'consumer' || p.type === 'consumer_prosumer') ? 'bg-emerald-100 text-emerald-700' :
                                      (p.type === 'producer' || p.type === 'prosumer') ? 'bg-amber-100 text-amber-700' :
                                      'bg-blue-100 text-blue-700'
                                    }`}>
                                      {p.label}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ── CREATE VIEW ── */}
              {simView === 'create' && (
                <div className="flex-1 overflow-hidden flex flex-col">
                  <div className="px-6 py-4 border-b border-neutral-100 shrink-0">
                    <div className="flex items-center justify-between mb-3">
                      <button onClick={resetSimWizard} className="flex items-center gap-1.5 text-sm text-neutral-500 hover:text-neutral-700 transition-colors">
                        <ChevronLeft className="w-4 h-4" />
                        Retour
                      </button>
                      <div className="flex items-center gap-2">
                        {[1, 2].map(step => (
                          <div key={step} className="flex items-center gap-1">
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                              simWizardStep === step ? 'bg-[#F8BD3D] text-neutral-900' :
                              simWizardStep > step ? 'bg-emerald-500 text-white' :
                              'bg-neutral-200 text-neutral-500'
                            }`}>
                              {simWizardStep > step ? <Check className="w-3 h-3" /> : step}
                            </div>
                            {step < 2 && <div className={`w-8 h-0.5 ${simWizardStep > step ? 'bg-emerald-400' : 'bg-neutral-200'}`} />}
                          </div>
                        ))}
                      </div>
                      <span className="text-xs text-neutral-400">Étape {simWizardStep}/2</span>
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-neutral-900">
                        {editingSimId
                          ? (simWizardStep === 1 ? 'Modifier la simulation' : 'Modifier les participants')
                          : (simWizardStep === 1 ? 'Paramètres généraux' : 'Configurer les participants')
                        }
                      </h3>
                      <p className="text-xs text-neutral-500 mt-0.5">
                        {simWizardStep === 1 ? 'Nommez votre simulation et définissez le nombre de participants' : 'Renseignez les informations de chaque participant'}
                      </p>
                    </div>
                  </div>

                  {simWizardStep === 1 && (
                    <div className="flex-1 overflow-y-auto p-6">
                      <div className="max-w-xl mx-auto space-y-5">
                        <div>
                          <label className="block text-sm font-semibold text-neutral-700 mb-2">Nom de la simulation</label>
                          <input
                            type="text"
                            value={simName}
                            onChange={e => setSimName(e.target.value)}
                            placeholder="Ex : Communauté Bruxelles - Projet 2025"
                            className="w-full px-4 py-3 text-sm border border-neutral-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#F8BD3D]/50 bg-white"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-neutral-700 mb-2">Région</label>
                          <div className="flex gap-2">
                            {(['bruxelles', 'wallonie'] as const).map(r => (
                              <button
                                key={r}
                                type="button"
                                onClick={() => { setSimRegion(r); setSimSharingType(''); setSimSharingScope(''); }}
                                className={`flex-1 py-2.5 rounded-xl text-sm font-semibold border transition-all ${simRegion === r ? 'border-[#F8BD3D] bg-[#F8BD3D]/10 text-neutral-800' : 'border-neutral-200 text-neutral-500 hover:border-neutral-300'}`}
                              >
                                {r === 'bruxelles' ? 'Bruxelles' : 'Wallonie'}
                              </button>
                            ))}
                          </div>
                        </div>
                        {simRegion === 'bruxelles' && (
                          <div>
                            <label className="block text-sm font-semibold text-neutral-700 mb-2">Type de partage</label>
                            <div className="grid grid-cols-2 gap-2">
                              {(['A', 'B', 'C', 'D'] as const).map(t => (
                                <button
                                  key={t}
                                  type="button"
                                  onClick={() => setSimSharingType(t)}
                                  className={`py-2.5 px-3 rounded-xl text-left border transition-all ${simSharingType === t ? 'border-[#F8BD3D] bg-[#F8BD3D]/10 text-neutral-800' : 'border-neutral-200 text-neutral-500 hover:border-neutral-300'}`}
                                >
                                  <span className="block text-sm font-bold">Type {t}</span>
                                  <span className="block text-[11px] font-normal mt-0.5 text-neutral-500">{SHARING_TYPE_DESCRIPTIONS[t]}</span>
                                  <span className="block text-[10px] font-semibold mt-0.5 text-amber-600">{NETWORK_REDUCTION_RATES[`bruxelles_${t}`] > 0 ? `−${NETWORK_REDUCTION_RATES[`bruxelles_${t}`]} €/MWh` : 'Aucune réduction réseau'}</span>
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                        {simRegion === 'wallonie' && (
                          <div>
                            <label className="block text-sm font-semibold text-neutral-700 mb-2">Périmètre de partage</label>
                            <div className="flex gap-2">
                              {([{ value: 'batiment', label: 'Au sein du même bâtiment', rate: 108 }, { value: 'communaute', label: "Communauté d'énergie", rate: 0 }] as const).map(s => (
                                <button
                                  key={s.value}
                                  type="button"
                                  onClick={() => setSimSharingScope(s.value)}
                                  className={`flex-1 py-2.5 px-3 rounded-xl text-left border transition-all ${simSharingScope === s.value ? 'border-[#F8BD3D] bg-[#F8BD3D]/10 text-neutral-800' : 'border-neutral-200 text-neutral-500 hover:border-neutral-300'}`}
                                >
                                  <span className="block text-sm font-semibold">{s.label}</span>
                                  <span className="block text-[10px] font-semibold mt-0.5 text-amber-600">{s.rate > 0 ? `−${s.rate} €/MWh` : 'Aucune réduction réseau'}</span>
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                        <div>
                          <label className="block text-sm font-semibold text-neutral-700 mb-2">Nombre de participants</label>
                          <div className="flex items-center gap-3">
                            <button
                              onClick={() => setSimParticipantCount(Math.max(1, simParticipantCount - 1))}
                              className="w-10 h-10 rounded-xl border border-neutral-200 flex items-center justify-center hover:bg-neutral-50 transition-colors text-neutral-600 font-bold text-lg"
                            >−</button>
                            <input
                              type="number"
                              min={1}
                              max={50}
                              value={simParticipantCount}
                              onChange={e => setSimParticipantCount(Math.max(1, Math.min(50, parseInt(e.target.value) || 1)))}
                              className="w-20 text-center px-3 py-2.5 text-sm font-semibold border border-neutral-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#F8BD3D]/50"
                            />
                            <button
                              onClick={() => setSimParticipantCount(Math.min(50, simParticipantCount + 1))}
                              className="w-10 h-10 rounded-xl border border-neutral-200 flex items-center justify-center hover:bg-neutral-50 transition-colors text-neutral-600 font-bold text-lg"
                            >+</button>
                            <span className="text-sm text-neutral-400">participant{simParticipantCount > 1 ? 's' : ''}</span>
                          </div>
                          <p className="text-xs text-neutral-400 mt-2">Maximum 50 participants par simulation</p>
                        </div>
                        <div className="pt-2">
                          <button
                            onClick={handleSimStep1Next}
                            className="w-full py-3.5 rounded-xl font-semibold text-sm text-neutral-900 flex items-center justify-center gap-2 transition-all hover:opacity-90"
                            style={{ backgroundColor: '#F8BD3D' }}
                          >
                            Continuer
                            <ChevronRight className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {simWizardStep === 2 && (
                    <div className="flex-1 overflow-y-auto p-6">
                      <div className="max-w-2xl mx-auto space-y-4">
                        {simParticipants.map((p, idx) => (
                          <div key={p.id} className="border border-neutral-200 rounded-2xl overflow-hidden">
                            <div className="px-4 py-3 bg-neutral-50 border-b border-neutral-100 space-y-2">
                              <div className="flex items-center gap-3">
                                <div className="w-7 h-7 rounded-lg bg-neutral-200 flex items-center justify-center text-xs font-bold text-neutral-600">{idx + 1}</div>
                                <input
                                  type="text"
                                  value={p.label}
                                  onChange={e => updateSimParticipant(idx, { label: e.target.value })}
                                  className="flex-1 text-sm font-semibold bg-transparent border-none outline-none text-neutral-800 placeholder:text-neutral-400"
                                  placeholder={`Participant ${idx + 1}`}
                                />
                                <div className="flex items-center gap-1 flex-wrap">
                                  {(['consumer', 'producer'] as const).map(t => {
                                    const isActive = t === 'consumer'
                                      ? (p.type === 'consumer' || p.type === 'consumer_prosumer')
                                      : (p.type === 'producer' || p.type === 'prosumer');
                                    return (
                                      <button
                                        key={t}
                                        onClick={() => {
                                          if (isActive) return;
                                          updateSimParticipant(idx, {
                                            type: t,
                                            supplierBuyRate: t === 'producer' ? '25' : '160',
                                            communityRate: t === 'producer' ? '65' : '95',
                                          });
                                        }}
                                        className={`px-2.5 py-1 rounded-lg text-[11px] font-semibold transition-all ${
                                          isActive
                                            ? t === 'consumer' ? 'bg-emerald-500 text-white' : 'bg-amber-400 text-neutral-900'
                                            : 'bg-white border border-neutral-200 text-neutral-500 hover:border-neutral-300'
                                        }`}
                                      >
                                        {t === 'consumer' ? 'Conso.' : 'Prod.'}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                              <input
                                type="text"
                                value={p.address}
                                onChange={e => updateSimParticipant(idx, { address: e.target.value })}
                                className="w-full px-3 py-1.5 text-xs border border-neutral-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-neutral-300/50 text-neutral-700 placeholder:text-neutral-400"
                                placeholder="Adresse (pour la carte)"
                              />
                            </div>
                            <div className="p-4 space-y-4">
                              {(p.type === 'consumer' || p.type === 'consumer_prosumer' || p.type === 'mixed') && (
                                <div className="space-y-3">
                                  <div className="flex items-center gap-2">
                                    <Building2 className="w-3.5 h-3.5 text-emerald-500" />
                                    <span className="text-xs font-semibold text-neutral-700">Consommation</span>
                                  </div>
                                  {p.type !== 'consumer_prosumer' && !p.consumptionFileName && (
                                  <div>
                                    <div className="flex items-center justify-between mb-1.5">
                                      <label className="text-[11px] text-neutral-500">Type de profil</label>
                                      {!loadingProfiles && simConsumerProfiles.length === 0 && (
                                        <button type="button" onClick={loadSheetProfiles} className="text-[10px] text-emerald-600 hover:underline">Recharger les profils</button>
                                      )}
                                    </div>
                                    <select
                                      value={p.profileType}
                                      onChange={e => updateSimParticipant(idx, { profileType: e.target.value as SimProfileValue })}
                                      className="w-full px-3 py-2 text-xs border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-300/50 bg-white appearance-none"
                                      style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%23999' stroke-width='2'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E\")", backgroundRepeat: 'no-repeat', backgroundPosition: 'right 8px center' }}
                                      disabled={loadingProfiles}
                                    >
                                      <option value="">{loadingProfiles ? 'Chargement...' : simConsumerProfiles.length === 0 ? 'Aucun profil chargé' : 'Sélectionner un profil...'}</option>
                                      {simConsumerProfiles.map(sp => (
                                        <option key={sp.value} value={sp.value}>{sp.label}</option>
                                      ))}
                                    </select>
                                  </div>
                                  )}
                                  {p.type !== 'consumer_prosumer' && (
                                  <div>
                                    <label className="block text-[11px] text-neutral-500 mb-1.5">Données de consommation</label>
                                    <div className="flex gap-2">
                                      <div className="flex-1 relative">
                                        <input
                                          type="number"
                                          step="0.1"
                                          value={p.annualConsumption}
                                          onChange={e => updateSimParticipant(idx, { annualConsumption: e.target.value, consumptionFile: null, consumptionFileName: '' })}
                                          placeholder="Consommation annuelle"
                                          disabled={!!p.consumptionFileName}
                                          className="w-full px-3 py-2 text-xs border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-300/50 disabled:bg-neutral-50 disabled:text-neutral-400"
                                        />
                                        <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] text-neutral-400">MWh/an</span>
                                      </div>
                                      <label className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium border cursor-pointer transition-all ${p.consumptionFileName ? 'border-emerald-400 bg-emerald-50 text-emerald-700' : 'border-neutral-200 text-neutral-500 hover:border-neutral-300 hover:bg-neutral-50'}`}>
                                        <input type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={e => { const f = e.target.files?.[0] || null; updateSimParticipant(idx, { consumptionFile: f, consumptionFileName: f?.name || '', annualConsumption: '' }); }} />
                                        {p.consumptionFileName ? (
                                          <><CheckCircle className="w-3 h-3" /><span className="max-w-[80px] truncate">{p.consumptionFileName}</span><button type="button" onClick={e => { e.preventDefault(); updateSimParticipant(idx, { consumptionFile: null, consumptionFileName: '' }); }} className="ml-0.5 hover:text-red-500"><X className="w-3 h-3" /></button></>
                                        ) : (
                                          <><Upload className="w-3 h-3" />Profil QH</>
                                        )}
                                      </label>
                                      <button
                                        type="button"
                                        onClick={() => { setPasteText(''); setPasteError(''); setPasteModal({ participantIdx: idx, kind: 'conso', source: 'wizard' }); }}
                                        className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium border border-neutral-200 text-neutral-500 hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-700 transition-all"
                                        title="Coller les valeurs quart-horaires"
                                      >
                                        <ClipboardPaste className="w-3 h-3" />Coller
                                      </button>
                                    </div>
                                    <div className="flex items-center justify-between mt-1">
                                      <p className="text-[10px] text-neutral-400">Col. 1 : datetime, col. 2 : valeur (MWh) — 35 040 lignes</p>
                                      <button type="button" onClick={downloadQhTemplate} className="flex items-center gap-1 text-[10px] text-emerald-600 hover:text-emerald-700 font-medium">
                                        <Download className="w-3 h-3" />Télécharger template
                                      </button>
                                    </div>
                                  </div>
                                  )}
                                  <div className="grid grid-cols-2 gap-2">
                                    <div className="relative">
                                      <label className="block text-[11px] text-neutral-500 mb-1.5">Tarif achat fournisseur + CRE</label>
                                      <input
                                        type="number"
                                        step="0.001"
                                        value={p.supplierBuyRate}
                                        onChange={e => updateSimParticipant(idx, { supplierBuyRate: e.target.value })}
                                        placeholder="0.280"
                                        className="w-full px-3 py-2 pr-12 text-xs border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-300/50"
                                      />
                                      <span className="absolute right-2.5 bottom-2 text-[10px] text-neutral-400">€/MWh</span>
                                    </div>
                                    <div className="relative">
                                      <label className="block text-[11px] text-neutral-500 mb-1.5">Tarif achat énergie partagée</label>
                                      <input
                                        type="number"
                                        step="0.001"
                                        value={p.communityRate}
                                        onChange={e => updateSimParticipant(idx, { communityRate: e.target.value })}
                                        placeholder="0.120"
                                        className="w-full px-3 py-2 pr-12 text-xs border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-300/50"
                                      />
                                      <span className="absolute right-2.5 bottom-2 text-[10px] text-neutral-400">€/MWh</span>
                                    </div>
                                  </div>
                                  {p.type === 'consumer_prosumer' && (
                                    <div className="grid grid-cols-2 gap-2">
                                      <div className="relative">
                                        <label className="block text-[11px] text-neutral-500 mb-1.5">Tarif vente surplus au fournisseur</label>
                                        <input
                                          type="number"
                                          step="0.001"
                                          value={p.supplierSellRate}
                                          onChange={e => updateSimParticipant(idx, { supplierSellRate: e.target.value })}
                                          placeholder="25"
                                          className="w-full px-3 py-2 pr-12 text-xs border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-300/50"
                                        />
                                        <span className="absolute right-2.5 bottom-2 text-[10px] text-neutral-400">€/MWh</span>
                                      </div>
                                      <div className="relative">
                                        <label className="block text-[11px] text-neutral-500 mb-1.5">Tarif vente surplus à la comm.</label>
                                        <input
                                          type="number"
                                          step="0.001"
                                          value={p.communityBuyRate}
                                          onChange={e => updateSimParticipant(idx, { communityBuyRate: e.target.value })}
                                          placeholder="65"
                                          className="w-full px-3 py-2 pr-12 text-xs border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-300/50"
                                        />
                                        <span className="absolute right-2.5 bottom-2 text-[10px] text-neutral-400">€/MWh</span>
                                      </div>
                                    </div>
                                  )}
                                  {p.type !== 'mixed' && (
                                    <label className="flex items-center gap-2.5 cursor-pointer select-none pt-1">
                                      <input
                                        type="checkbox"
                                        checked={p.type === 'consumer_prosumer'}
                                        onChange={e => {
                                          const injectionProfile = simProducerProfiles.find(sp => sp.label.toLowerCase().includes('injection'));
                                          updateSimParticipant(idx, {
                                            type: e.target.checked ? 'consumer_prosumer' : 'consumer',
                                            ...(e.target.checked ? { producerProfileType: (injectionProfile?.value ?? '') as SimProducerProfileValue } : {}),
                                          });
                                        }}
                                        className="w-3.5 h-3.5 rounded border-neutral-300 text-emerald-500 focus:ring-emerald-300/50"
                                      />
                                      <span className="text-[11px] text-neutral-600 font-medium">Le consommateur possède sa propre installation PV</span>
                                    </label>
                                  )}
                                  {p.type === 'consumer_prosumer' && (
                                    <div className="space-y-3 pt-1 pl-3 border-l-2 border-emerald-200">
                                      <div className="p-2.5 rounded-lg bg-emerald-50 border border-emerald-100">
                                        <p className="text-[10px] text-emerald-700">Le profil de consommation net sera calculé automatiquement : consommation sur site − production PV × puissance × 0.95 (valeurs négatives = 0).</p>
                                      </div>
                                      <div>
                                        <label className="block text-[11px] text-neutral-500 mb-1.5">Profil de consommation sur site</label>
                                        <select
                                          value={p.profileType}
                                          onChange={e => updateSimParticipant(idx, { profileType: e.target.value as SimProfileValue })}
                                          className="w-full px-3 py-2 text-xs border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-300/50 bg-white appearance-none"
                                          style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%23999' stroke-width='2'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E\")", backgroundRepeat: 'no-repeat', backgroundPosition: 'right 8px center' }}
                                          disabled={loadingProfiles}
                                        >
                                          <option value="">{loadingProfiles ? 'Chargement...' : simConsumerProfiles.length === 0 ? 'Aucun profil chargé' : 'Sélectionner un profil de conso...'}</option>
                                          {simConsumerProfiles.map(sp => (
                                            <option key={sp.value} value={sp.value}>{sp.label}</option>
                                          ))}
                                        </select>
                                      </div>
                                      <div className="relative">
                                        <label className="block text-[11px] text-neutral-500 mb-1.5">Consommation annuelle sur site</label>
                                        <input
                                          type="number"
                                          step="0.1"
                                          value={p.annualConsumption}
                                          onChange={e => updateSimParticipant(idx, { annualConsumption: e.target.value })}
                                          placeholder="Ex: 120"
                                          className="w-full px-3 py-2 pr-14 text-xs border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-300/50"
                                        />
                                        <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] text-neutral-400">MWh/an</span>
                                      </div>
                                      <div className="relative">
                                        <label className="block text-[11px] text-neutral-500 mb-1.5">Puissance installée</label>
                                        <input
                                          type="number"
                                          step="0.1"
                                          value={p.prosumerInstalledPower}
                                          onChange={e => updateSimParticipant(idx, { prosumerInstalledPower: e.target.value })}
                                          placeholder="Ex: 50"
                                          className="w-full px-3 py-2 pr-14 text-xs border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-300/50"
                                        />
                                        <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] text-neutral-400">kWc</span>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                              {(p.type === 'mixed') && <div className="h-px bg-neutral-100" />}
                              {(p.type === 'producer' || p.type === 'prosumer' || p.type === 'mixed') && (
                                <div className="space-y-3">
                                  <div className="flex items-center gap-2">
                                    <Sun className="w-3.5 h-3.5 text-amber-500" />
                                    <span className="text-xs font-semibold text-neutral-700">Production / Injection</span>
                                  </div>
                                  {p.type !== 'prosumer' && !p.injectionFileName && (
                                  <div>
                                    <label className="block text-[11px] text-neutral-500 mb-1.5">Type de profil de production</label>
                                    <select
                                      value={p.producerProfileType}
                                      onChange={e => updateSimParticipant(idx, { producerProfileType: e.target.value as SimProducerProfileValue })}
                                      className="w-full px-3 py-2 text-xs border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-300/50 bg-white appearance-none"
                                      style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%23999' stroke-width='2'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E\")", backgroundRepeat: 'no-repeat', backgroundPosition: 'right 8px center' }}
                                      disabled={loadingProfiles}
                                    >
                                      <option value="">{loadingProfiles ? 'Chargement...' : 'Sélectionner un profil...'}</option>
                                      {simProducerProfiles.map(sp => (
                                        <option key={sp.value} value={sp.value}>{sp.label}</option>
                                      ))}
                                    </select>
                                  </div>
                                  )}
                                  {p.type !== 'prosumer' && (
                                  <div>
                                    <label className="block text-[11px] text-neutral-500 mb-1.5">Données d'injection</label>
                                    <div className="flex gap-2">
                                      <div className="flex-1 relative">
                                        <input
                                          type="number"
                                          step="0.1"
                                          value={p.annualInjection}
                                          onChange={e => updateSimParticipant(idx, { annualInjection: e.target.value, injectionFile: null, injectionFileName: '' })}
                                          placeholder="Injection annuelle"
                                          disabled={!!p.injectionFileName}
                                          className="w-full px-3 py-2 text-xs border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-300/50 disabled:bg-neutral-50 disabled:text-neutral-400"
                                        />
                                        <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] text-neutral-400">MWh/an</span>
                                      </div>
                                      <label className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium border cursor-pointer transition-all ${p.injectionFileName ? 'border-amber-400 bg-amber-50 text-amber-700' : 'border-neutral-200 text-neutral-500 hover:border-neutral-300 hover:bg-neutral-50'}`}>
                                        <input type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={e => { const f = e.target.files?.[0] || null; updateSimParticipant(idx, { injectionFile: f, injectionFileName: f?.name || '', annualInjection: '' }); }} />
                                        {p.injectionFileName ? (
                                          <><CheckCircle className="w-3 h-3" /><span className="max-w-[80px] truncate">{p.injectionFileName}</span><button type="button" onClick={e => { e.preventDefault(); updateSimParticipant(idx, { injectionFile: null, injectionFileName: '' }); }} className="ml-0.5 hover:text-red-500"><X className="w-3 h-3" /></button></>
                                        ) : (
                                          <><Upload className="w-3 h-3" />Profil QH</>
                                        )}
                                      </label>
                                      <button
                                        type="button"
                                        onClick={() => { setPasteText(''); setPasteError(''); setPasteModal({ participantIdx: idx, kind: 'injection', source: 'wizard' }); }}
                                        className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium border border-neutral-200 text-neutral-500 hover:border-amber-300 hover:bg-amber-50 hover:text-amber-700 transition-all"
                                        title="Coller les valeurs quart-horaires"
                                      >
                                        <ClipboardPaste className="w-3 h-3" />Coller
                                      </button>
                                    </div>
                                    <div className="flex items-center justify-between mt-1">
                                      <p className="text-[10px] text-neutral-400">Col. 1 : datetime, col. 2 : valeur (MWh) — 35 040 lignes</p>
                                      <button type="button" onClick={downloadQhTemplate} className="flex items-center gap-1 text-[10px] text-amber-600 hover:text-amber-700 font-medium">
                                        <Download className="w-3 h-3" />Télécharger template
                                      </button>
                                    </div>
                                  </div>
                                  )}
                                  {(p.type === 'producer' || p.type === 'prosumer') && (
                                    <div className="grid grid-cols-2 gap-2">
                                      <div className="relative">
                                        <label className="block text-[11px] text-neutral-500 mb-1.5">Tarif vente au fournisseur</label>
                                        <input
                                          type="number"
                                          step="0.001"
                                          value={p.supplierSellRate}
                                          onChange={e => updateSimParticipant(idx, { supplierSellRate: e.target.value })}
                                          placeholder="25"
                                          className="w-full px-3 py-2 pr-12 text-xs border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-300/50"
                                        />
                                        <span className="absolute right-2.5 bottom-2 text-[10px] text-neutral-400">€/MWh</span>
                                      </div>
                                      <div className="relative">
                                        <label className="block text-[11px] text-neutral-500 mb-1.5">Tarif vente CE</label>
                                        <input
                                          type="number"
                                          step="0.001"
                                          value={p.communityBuyRate}
                                          onChange={e => updateSimParticipant(idx, { communityBuyRate: e.target.value })}
                                          placeholder="65"
                                          className="w-full px-3 py-2 pr-12 text-xs border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-300/50"
                                        />
                                        <span className="absolute right-2.5 bottom-2 text-[10px] text-neutral-400">€/MWh</span>
                                      </div>
                                    </div>
                                  )}
                                  {p.type !== 'mixed' && (
                                    <label className="flex items-center gap-2.5 cursor-pointer select-none pt-1">
                                      <input
                                        type="checkbox"
                                        checked={p.type === 'prosumer'}
                                        onChange={e => {
                                          const injectionProfile = simProducerProfiles.find(sp => sp.label.toLowerCase().includes('injection'));
                                          updateSimParticipant(idx, {
                                            type: e.target.checked ? 'prosumer' : 'producer',
                                            ...(e.target.checked ? { producerProfileType: (injectionProfile?.value ?? '') as SimProducerProfileValue } : {}),
                                          });
                                        }}
                                        className="w-3.5 h-3.5 rounded border-neutral-300 text-amber-500 focus:ring-amber-300/50"
                                      />
                                      <span className="text-[11px] text-neutral-600 font-medium">Le producteur autoconsomme</span>
                                    </label>
                                  )}
                                  {p.type === 'prosumer' && (
                                    <div className="space-y-3 pt-1 pl-3 border-l-2 border-amber-200">
                                      <div className="p-2.5 rounded-lg bg-amber-50 border border-amber-100">
                                        <p className="text-[10px] text-amber-700">Le profil d'injection net sera calculé automatiquement : production PV × puissance × 0.95 − consommation sur site (valeurs négatives = 0).</p>
                                      </div>
                                      <div className="relative">
                                        <label className="block text-[11px] text-neutral-500 mb-1.5">Puissance installée</label>
                                        <input
                                          type="number"
                                          step="0.1"
                                          value={p.prosumerInstalledPower}
                                          onChange={e => updateSimParticipant(idx, { prosumerInstalledPower: e.target.value })}
                                          placeholder="Ex: 50"
                                          className="w-full px-3 py-2 pr-14 text-xs border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-300/50"
                                        />
                                        <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] text-neutral-400">kWc</span>
                                      </div>
                                      <div>
                                        <label className="block text-[11px] text-neutral-500 mb-1.5">Profil de consommation sur site</label>
                                        <select
                                          value={p.prosumerConsumptionProfileType}
                                          onChange={e => updateSimParticipant(idx, { prosumerConsumptionProfileType: e.target.value as SimProfileValue })}
                                          className="w-full px-3 py-2 text-xs border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-300/50 bg-white appearance-none"
                                          style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%23999' stroke-width='2'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E\")", backgroundRepeat: 'no-repeat', backgroundPosition: 'right 8px center' }}
                                          disabled={loadingProfiles}
                                        >
                                          <option value="">{loadingProfiles ? 'Chargement...' : simConsumerProfiles.length === 0 ? 'Aucun profil chargé' : 'Sélectionner un profil de conso...'}</option>
                                          {simConsumerProfiles.map(sp => (
                                            <option key={sp.value} value={sp.value}>{sp.label}</option>
                                          ))}
                                        </select>
                                      </div>
                                      <div className="relative">
                                        <label className="block text-[11px] text-neutral-500 mb-1.5">Consommation annuelle sur site</label>
                                        <input
                                          type="number"
                                          step="0.1"
                                          value={p.prosumerSiteConsumption}
                                          onChange={e => updateSimParticipant(idx, { prosumerSiteConsumption: e.target.value })}
                                          placeholder="Ex: 120"
                                          className="w-full px-3 py-2 pr-14 text-xs border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-300/50"
                                        />
                                        <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] text-neutral-400">MWh/an</span>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                        {simResult && (
                          <div className="mx-0 mb-3 rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-3">
                            <div className="flex items-center gap-3 mb-2">
                              <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                                <TrendingUp className="w-4 h-4 text-emerald-600" />
                              </div>
                              <div>
                                <p className="text-xs font-medium text-emerald-700">Résultat de la simulation</p>
                                <p className="text-base font-bold text-emerald-900">
                                  {simResult.totalShared.toFixed(2)} MWh partagés / an
                                </p>
                              </div>
                            </div>
                            {simResult.hasGains && (
                              <div className="mt-1 pt-2 border-t border-emerald-200 flex items-center justify-between">
                                <span className="text-xs text-emerald-700 font-medium">Gains économiques estimés / an</span>
                                <span className="text-sm font-bold text-emerald-900">{simResult.totalGains.toFixed(2)} €</span>
                              </div>
                            )}
                          </div>
                        )}
                        <div className="flex gap-3 pt-2 pb-4">
                          <button
                            onClick={() => { setSimWizardStep(1); setSimResult(null); }}
                            className="py-3 px-4 rounded-xl border border-neutral-200 text-sm font-medium text-neutral-600 hover:bg-neutral-50 transition-colors"
                          >
                            Retour
                          </button>
                          <button
                            onClick={handleRunSimulation}
                            disabled={runningSim || savingSim}
                            className="flex-1 py-3 rounded-xl font-semibold text-sm text-white flex items-center justify-center gap-2 transition-all hover:opacity-90 disabled:opacity-60 bg-emerald-600 hover:bg-emerald-700"
                          >
                            {runningSim ? <><Loader2 className="w-4 h-4 animate-spin" />Calcul en cours...</> : <><Calculator className="w-4 h-4" />Lancer la simulation</>}
                          </button>
                          {simResult && (
                            <button
                              onClick={() => setShowSimChart(true)}
                              className="py-3 px-4 rounded-xl font-semibold text-sm text-emerald-700 border border-emerald-300 bg-emerald-50 flex items-center justify-center gap-2 transition-all hover:bg-emerald-100"
                            >
                              <BarChart3 className="w-4 h-4" />
                              Graphiques
                            </button>
                          )}
                          <button
                            onClick={handleSaveSimulation}
                            disabled={savingSim || runningSim}
                            className="flex-1 py-3 rounded-xl font-semibold text-sm text-neutral-900 flex items-center justify-center gap-2 transition-all hover:opacity-90 disabled:opacity-60"
                            style={{ backgroundColor: '#F8BD3D' }}
                          >
                            {savingSim ? <><Loader2 className="w-4 h-4 animate-spin" />{editingSimId ? 'Mise à jour...' : 'Sauvegarde...'}</> : <><Save className="w-4 h-4" />{editingSimId ? 'Mettre à jour' : 'Enregistrer'}</>}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
        </div>
      </div>

      {/* Simulation loading overlay */}
      {runningSim && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
          <div className="relative bg-white rounded-2xl shadow-2xl px-10 py-8 flex flex-col items-center gap-5 min-w-[280px]">
            <div className="relative flex items-center justify-center">
              <div className="w-14 h-14 rounded-full border-4 border-emerald-100 border-t-emerald-500 animate-spin" />
              <Calculator className="absolute w-5 h-5 text-emerald-600" />
            </div>
            <div className="text-center">
              <p className="text-sm font-semibold text-neutral-900">Calcul en cours…</p>
              <p className="text-xs text-neutral-400 mt-1">Analyse des 35 040 quarts d'heure</p>
            </div>
          </div>
        </div>
      )}

      {/* Simulation Chart Modal */}
      {showSimChart && simResult && (
        <SimulationChartModal
          data={simResult.monthlyData}
          participantMonthlyData={simResult.participantMonthlyData}
          totalShared={simResult.totalShared}
          totalConsumption={simResult.totalConsumption}
          totalProduction={simResult.totalProduction}
          totalGrossConsumption={simResult.totalGrossConsumption ?? simResult.totalConsumption}
          totalGrossProduction={simResult.totalGrossProduction ?? simResult.totalProduction}
          totalAutoConsumption={simResult.totalAutoConsumption ?? 0}
          totalGains={simResult.totalGains}
          totalCommodityGains={simResult.totalCommodityGains}
          totalNetworkGains={simResult.totalNetworkGains}
          networkReductionRate={simResult.networkReductionRate}
          hasGains={simResult.hasGains}
          simName={simResult.simName || simName}
          qhData={simQHData || undefined}
          onClose={() => setShowSimChart(false)}
        />
      )}

      {/* Simulation Chart Modal - from saved list */}
      {viewingSimResult && (
        <SimulationChartModal
          data={viewingSimResult.monthlyData}
          participantMonthlyData={viewingSimResult.participantMonthlyData}
          totalShared={viewingSimResult.totalShared}
          totalConsumption={viewingSimResult.totalConsumption}
          totalProduction={viewingSimResult.totalProduction}
          totalGrossConsumption={(viewingSimResult as SimResultData & { simName: string }).totalGrossConsumption ?? viewingSimResult.totalConsumption}
          totalGrossProduction={(viewingSimResult as SimResultData & { simName: string }).totalGrossProduction ?? viewingSimResult.totalProduction}
          totalAutoConsumption={(viewingSimResult as SimResultData & { simName: string }).totalAutoConsumption ?? 0}
          totalGains={viewingSimResult.totalGains}
          totalCommodityGains={(viewingSimResult as SimResultData & { simName: string }).totalCommodityGains}
          totalNetworkGains={(viewingSimResult as SimResultData & { simName: string }).totalNetworkGains}
          networkReductionRate={(viewingSimResult as SimResultData & { simName: string }).networkReductionRate}
          hasGains={viewingSimResult.hasGains}
          simName={viewingSimResult.simName}
          onClose={() => setViewingSimResult(null)}
        />
      )}

      {/* QH Paste Modal */}
      {pasteModal && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => { setPasteModal(null); setPasteText(''); setPasteError(''); }} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col overflow-hidden" style={{ maxHeight: '90vh' }}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-100">
              <div>
                <h3 className="text-sm font-semibold text-neutral-900">Coller les données quart-horaires</h3>
                <p className="text-xs text-neutral-500 mt-0.5">
                  {pasteModal.kind === 'conso' ? 'Consommation' : 'Injection'} — participant {pasteModal.participantIdx + 1}
                </p>
              </div>
              <button onClick={() => { setPasteModal(null); setPasteText(''); setPasteError(''); }} className="p-1.5 rounded-lg hover:bg-neutral-100 text-neutral-400">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="px-6 py-4 flex-1 overflow-y-auto">
              <p className="text-xs text-neutral-500 mb-3">
                Collez vos valeurs ci-dessous — une valeur par ligne (MWh), du 1er janvier au 31 décembre.
                Les valeurs manquantes seront remplacées par 0. Maximum 35 040 valeurs.
              </p>
              <textarea
                value={pasteText}
                onChange={e => { setPasteText(e.target.value); setPasteError(''); }}
                placeholder={'0.0124\n0.0118\n0.0095\n...'}
                className="w-full h-64 px-3 py-2.5 text-xs font-mono border border-neutral-200 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-emerald-300/50"
                autoFocus
              />
              {pasteError && (
                <p className="mt-2 text-xs text-red-600 flex items-center gap-1.5">
                  <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />{pasteError}
                </p>
              )}
              {pasteText.trim() && !pasteError && (
                <p className="mt-2 text-xs text-neutral-400">
                  {pasteText.trim().split(/[\n\r]+/).filter(l => l.trim()).length} valeurs détectées
                  {pasteText.trim().split(/[\n\r]+/).filter(l => l.trim()).length < 35040 && (
                    <span className="text-amber-600 ml-1">— les valeurs manquantes seront complétées par 0</span>
                  )}
                </p>
              )}
            </div>
            <div className="px-6 py-4 border-t border-neutral-100 flex justify-end gap-3 bg-neutral-50/60">
              <button
                onClick={() => { setPasteModal(null); setPasteText(''); setPasteError(''); }}
                className="py-2 px-4 rounded-xl border border-neutral-200 text-sm font-medium text-neutral-600 hover:bg-neutral-100 transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handlePasteConfirm}
                disabled={!pasteText.trim()}
                className="py-2 px-5 rounded-xl text-sm font-semibold text-neutral-900 flex items-center gap-2 transition-all hover:opacity-90 disabled:opacity-40"
                style={{ backgroundColor: '#F8BD3D' }}
              >
                <ClipboardPaste className="w-4 h-4" />
                Importer les valeurs
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Participant Manager Overlay */}
      {participantManagerSim && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setParticipantManagerSim(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col overflow-hidden" style={{ maxHeight: '90vh' }}>
            {/* Header */}
            <div className="px-6 py-4 border-b border-neutral-100 flex items-center justify-between flex-shrink-0">
              <div>
                <h3 className="text-base font-semibold text-neutral-900">Participants</h3>
                <p className="text-xs text-neutral-500 mt-0.5">{participantManagerSim.name}</p>
              </div>
              <button onClick={() => setParticipantManagerSim(null)} className="p-1.5 rounded-lg hover:bg-neutral-100 text-neutral-400 hover:text-neutral-600 transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Participant list */}
            <div className="flex-1 overflow-y-auto p-5 space-y-3">
              {pmParticipants.length === 0 && (
                <div className="text-center py-8 text-sm text-neutral-400">Aucun participant — ajoutez-en un ci-dessous.</div>
              )}
              {pmParticipants.map((p, idx) => (
                <div key={p.id} className="border border-neutral-200 rounded-2xl overflow-hidden">
                  {/* Row header */}
                  <div className="px-4 py-3 bg-neutral-50 border-b border-neutral-100 space-y-2">
                    <div className="flex items-center gap-3">
                      <div className="w-7 h-7 rounded-lg bg-neutral-200 flex items-center justify-center text-xs font-bold text-neutral-600 flex-shrink-0">{idx + 1}</div>
                      <input
                        type="text"
                        value={p.label}
                        onChange={e => updatePmParticipant(idx, { label: e.target.value })}
                        className="flex-1 text-sm font-semibold bg-transparent border-none outline-none text-neutral-800 placeholder:text-neutral-400"
                        placeholder={`Participant ${idx + 1}`}
                      />
                      <div className="flex items-center gap-1">
                        {(['consumer', 'producer'] as const).map(t => {
                          const isActive = t === 'consumer'
                            ? (p.type === 'consumer' || p.type === 'consumer_prosumer')
                            : (p.type === 'producer' || p.type === 'prosumer');
                          return (
                            <button
                              key={t}
                              onClick={() => {
                                if (isActive) return;
                                const wasAutocons = p.type === 'consumer_prosumer' || p.type === 'prosumer';
                                updatePmParticipant(idx, {
                                  type: wasAutocons ? (t === 'consumer' ? 'consumer_prosumer' : 'prosumer') : t,
                                  supplierBuyRate: t === 'producer' ? '25' : '160',
                                  communityRate: t === 'producer' ? '65' : '95',
                                });
                              }}
                              className={`px-2.5 py-1 rounded-lg text-[11px] font-semibold transition-all ${
                                isActive
                                  ? t === 'consumer' ? 'bg-emerald-500 text-white' : 'bg-amber-400 text-neutral-900'
                                  : 'bg-white border border-neutral-200 text-neutral-500 hover:border-neutral-300'
                              }`}
                            >
                              {t === 'consumer' ? 'Conso.' : 'Prod.'}
                            </button>
                          );
                        })}
                        <label className="flex items-center gap-1.5 cursor-pointer select-none ml-1" title="Cochez si ce participant possède une installation PV qui autoconsomme">
                          <input
                            type="checkbox"
                            checked={p.type === 'prosumer' || p.type === 'consumer_prosumer'}
                            onChange={e => {
                              const checked = e.target.checked;
                              if (checked) {
                                const injectionProfile = simProducerProfiles.find(sp => sp.label.toLowerCase().includes('injection'));
                                updatePmParticipant(idx, {
                                  type: p.type === 'consumer' || p.type === 'consumer_prosumer' ? 'consumer_prosumer' : 'prosumer',
                                  ...(p.producerProfileType ? {} : { producerProfileType: (injectionProfile?.value ?? '') as SimProducerProfileValue }),
                                });
                              } else {
                                updatePmParticipant(idx, {
                                  type: p.type === 'consumer_prosumer' ? 'consumer' : 'producer',
                                });
                              }
                            }}
                            className="w-3.5 h-3.5 rounded border-neutral-300 text-amber-500 focus:ring-amber-300/50"
                          />
                          <span className="text-[11px] text-neutral-600 font-medium">PV</span>
                        </label>
                      </div>
                      <button
                        onClick={() => removePmParticipant(idx)}
                        className="p-1.5 rounded-lg hover:bg-red-50 hover:text-red-500 text-neutral-400 transition-colors flex-shrink-0"
                        title="Retirer ce participant"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <input
                      type="text"
                      value={p.address}
                      onChange={e => updatePmParticipant(idx, { address: e.target.value })}
                      className="w-full px-3 py-1.5 text-xs border border-neutral-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-neutral-300/50 text-neutral-700 placeholder:text-neutral-400"
                      placeholder="Adresse (pour la carte)"
                    />
                  </div>

                  {/* Config body */}
                  <div className="p-4 space-y-4">
                    {(p.type === 'consumer' || p.type === 'consumer_prosumer' || p.type === 'mixed' || p.type === 'prosumer') && (
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <Building2 className="w-3.5 h-3.5 text-emerald-500" />
                          <span className="text-xs font-semibold text-neutral-700">Consommation</span>
                        </div>
                        {p.type === 'prosumer' ? (
                          <>
                            <div>
                              <label className="block text-[11px] text-neutral-500 mb-1.5">Type de profil de consommation sur site</label>
                              <select
                                value={p.prosumerConsumptionProfileType}
                                onChange={e => updatePmParticipant(idx, { prosumerConsumptionProfileType: e.target.value as SimProfileValue })}
                                className="w-full px-3 py-2 text-xs border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-300/50 bg-white appearance-none"
                                style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%23999' stroke-width='2'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E\")", backgroundRepeat: 'no-repeat', backgroundPosition: 'right 8px center' }}
                                disabled={loadingProfiles}
                              >
                                <option value="">{loadingProfiles ? 'Chargement...' : simConsumerProfiles.length === 0 ? 'Aucun profil chargé' : 'Sélectionner un profil...'}</option>
                                {simConsumerProfiles.map(sp => (
                                  <option key={sp.value} value={sp.value}>{sp.label}</option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-[11px] text-neutral-500 mb-1.5">Consommation annuelle sur site</label>
                              <div className="relative">
                                <input
                                  type="number"
                                  step="0.1"
                                  value={p.prosumerSiteConsumption}
                                  onChange={e => updatePmParticipant(idx, { prosumerSiteConsumption: e.target.value })}
                                  placeholder="Consommation annuelle sur site"
                                  className="w-full px-3 py-2 pr-14 text-xs border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-300/50"
                                />
                                <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] text-neutral-400">MWh/an</span>
                              </div>
                            </div>
                          </>
                        ) : (
                          <>
                            {!p.consumptionFileName && (
                              <div>
                                <label className="block text-[11px] text-neutral-500 mb-1.5">Type de profil</label>
                                <select
                                  value={p.profileType}
                                  onChange={e => updatePmParticipant(idx, { profileType: e.target.value as SimProfileValue })}
                                  className="w-full px-3 py-2 text-xs border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-300/50 bg-white appearance-none"
                                  style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%23999' stroke-width='2'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E\")", backgroundRepeat: 'no-repeat', backgroundPosition: 'right 8px center' }}
                                  disabled={loadingProfiles}
                                >
                                  <option value="">{loadingProfiles ? 'Chargement...' : simConsumerProfiles.length === 0 ? 'Aucun profil chargé' : 'Sélectionner un profil...'}</option>
                                  {simConsumerProfiles.map(sp => (
                                    <option key={sp.value} value={sp.value}>{sp.label}</option>
                                  ))}
                                </select>
                              </div>
                            )}
                            <div>
                              <label className="block text-[11px] text-neutral-500 mb-1.5">Données de consommation</label>
                              <div className="flex gap-2">
                                <div className="flex-1 relative">
                                  <input
                                    type="number"
                                    step="0.1"
                                    value={p.annualConsumption}
                                    onChange={e => updatePmParticipant(idx, { annualConsumption: e.target.value, consumptionFile: null, consumptionFileName: '' })}
                                    placeholder="Consommation annuelle"
                                    disabled={!!p.consumptionFileName}
                                    className="w-full px-3 py-2 pr-14 text-xs border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-300/50 disabled:bg-neutral-50 disabled:text-neutral-400"
                                  />
                                  <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] text-neutral-400">MWh/an</span>
                                </div>
                                <label className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium border cursor-pointer transition-all ${p.consumptionFileName ? 'border-emerald-400 bg-emerald-50 text-emerald-700' : 'border-neutral-200 text-neutral-500 hover:border-neutral-300 hover:bg-neutral-50'}`}>
                                  <input type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={e => { const f = e.target.files?.[0] || null; updatePmParticipant(idx, { consumptionFile: f, consumptionFileName: f?.name || '', annualConsumption: '' }); }} />
                                  {p.consumptionFileName ? (
                                    <><CheckCircle className="w-3 h-3" /><span className="max-w-[80px] truncate">{p.consumptionFileName}</span><button type="button" onClick={e => { e.preventDefault(); updatePmParticipant(idx, { consumptionFile: null, consumptionFileName: '' }); }} className="ml-0.5 hover:text-red-500"><X className="w-3 h-3" /></button></>
                                  ) : (
                                    <><Upload className="w-3 h-3" />Profil QH</>
                                  )}
                                </label>
                                <button
                                  type="button"
                                  onClick={() => { setPasteText(''); setPasteError(''); setPasteModal({ participantIdx: idx, kind: 'conso', source: 'pm' }); }}
                                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium border border-neutral-200 text-neutral-500 hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-700 transition-all"
                                  title="Coller les valeurs quart-horaires"
                                >
                                  <ClipboardPaste className="w-3 h-3" />Coller
                                </button>
                              </div>
                            </div>
                          </>
                        )}
                        <div className="grid grid-cols-2 gap-2">
                          <div className="relative">
                            <label className="block text-[11px] text-neutral-500 mb-1.5">Tarif achat fournisseur + CRE</label>
                            <input type="number" step="0.001" value={p.supplierBuyRate} onChange={e => updatePmParticipant(idx, { supplierBuyRate: e.target.value })} placeholder="160" className="w-full px-3 py-2 pr-12 text-xs border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-300/50" />
                            <span className="absolute right-2.5 top-[calc(50%+8px)] -translate-y-1/2 text-[10px] text-neutral-400">€/MWh</span>
                          </div>
                          <div className="relative">
                            <label className="block text-[11px] text-neutral-500 mb-1.5">Tarif achat CE</label>
                            <input type="number" step="0.001" value={p.communityRate} onChange={e => updatePmParticipant(idx, { communityRate: e.target.value })} placeholder="95" className="w-full px-3 py-2 pr-12 text-xs border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-300/50" />
                            <span className="absolute right-2.5 top-[calc(50%+8px)] -translate-y-1/2 text-[10px] text-neutral-400">€/MWh</span>
                          </div>
                        </div>
                      </div>
                    )}
                    {(p.type === 'producer' || p.type === 'mixed' || p.type === 'prosumer' || p.type === 'consumer_prosumer') && (
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <Sun className="w-3.5 h-3.5 text-amber-500" />
                          <span className="text-xs font-semibold text-neutral-700">Production {p.type === 'prosumer' || p.type === 'consumer_prosumer' ? '(installation PV)' : ''}</span>
                        </div>
                        {(p.type === 'producer' || p.type === 'mixed') && !p.injectionFileName && (
                          <div>
                            <label className="block text-[11px] text-neutral-500 mb-1.5">Type de profil de production</label>
                            <select
                              value={p.producerProfileType}
                              onChange={e => updatePmParticipant(idx, { producerProfileType: e.target.value as SimProducerProfileValue })}
                              className="w-full px-3 py-2 text-xs border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-300/50 bg-white appearance-none"
                              style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%23999' stroke-width='2'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E\")", backgroundRepeat: 'no-repeat', backgroundPosition: 'right 8px center' }}
                              disabled={loadingProfiles}
                            >
                              <option value="">{loadingProfiles ? 'Chargement...' : simProducerProfiles.length === 0 ? 'Aucun profil chargé' : 'Sélectionner un profil...'}</option>
                              {simProducerProfiles.map(sp => (
                                <option key={sp.value} value={sp.value}>{sp.label}</option>
                              ))}
                            </select>
                          </div>
                        )}
                        {(p.type === 'prosumer' || p.type === 'consumer_prosumer') && (
                          <div>
                            <label className="block text-[11px] text-neutral-500 mb-1.5">Type de profil PV</label>
                            <select
                              value={p.producerProfileType}
                              onChange={e => updatePmParticipant(idx, { producerProfileType: e.target.value as SimProducerProfileValue })}
                              className="w-full px-3 py-2 text-xs border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-300/50 bg-white appearance-none"
                              style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%23999' stroke-width='2'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E\")", backgroundRepeat: 'no-repeat', backgroundPosition: 'right 8px center' }}
                              disabled={loadingProfiles}
                            >
                              <option value="">{loadingProfiles ? 'Chargement...' : simProducerProfiles.length === 0 ? 'Aucun profil chargé' : 'Sélectionner un profil...'}</option>
                              {simProducerProfiles.map(sp => (
                                <option key={sp.value} value={sp.value}>{sp.label}</option>
                              ))}
                            </select>
                          </div>
                        )}
                        {(p.type === 'prosumer' || p.type === 'consumer_prosumer') && (
                          <div>
                            <label className="block text-[11px] text-neutral-500 mb-1.5">Puissance installée</label>
                            <div className="relative">
                              <input
                                type="number"
                                step="0.1"
                                value={p.prosumerInstalledPower}
                                onChange={e => updatePmParticipant(idx, { prosumerInstalledPower: e.target.value })}
                                placeholder="Puissance installée"
                                className="w-full px-3 py-2 pr-14 text-xs border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-300/50"
                              />
                              <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] text-neutral-400">kWc</span>
                            </div>
                          </div>
                        )}
                        {(p.type === 'producer' || p.type === 'mixed') && (
                          <div>
                            <label className="block text-[11px] text-neutral-500 mb-1.5">Données d'injection</label>
                            <div className="flex gap-2">
                              <div className="flex-1 relative">
                                <input
                                  type="number"
                                  step="0.1"
                                  value={p.annualInjection}
                                  onChange={e => updatePmParticipant(idx, { annualInjection: e.target.value, injectionFile: null, injectionFileName: '' })}
                                  placeholder="Injection annuelle"
                                  disabled={!!p.injectionFileName}
                                  className="w-full px-3 py-2 pr-14 text-xs border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-300/50 disabled:bg-neutral-50 disabled:text-neutral-400"
                                />
                                <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] text-neutral-400">MWh/an</span>
                              </div>
                              <label className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium border cursor-pointer transition-all ${p.injectionFileName ? 'border-amber-400 bg-amber-50 text-amber-700' : 'border-neutral-200 text-neutral-500 hover:border-neutral-300 hover:bg-neutral-50'}`}>
                                <input type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={e => { const f = e.target.files?.[0] || null; updatePmParticipant(idx, { injectionFile: f, injectionFileName: f?.name || '', annualInjection: '' }); }} />
                                {p.injectionFileName ? (
                                  <><CheckCircle className="w-3 h-3" /><span className="max-w-[80px] truncate">{p.injectionFileName}</span><button type="button" onClick={e => { e.preventDefault(); updatePmParticipant(idx, { injectionFile: null, injectionFileName: '' }); }} className="ml-0.5 hover:text-red-500"><X className="w-3 h-3" /></button></>
                                ) : (
                                  <><Upload className="w-3 h-3" />Profil QH</>
                                )}
                              </label>
                              <button
                                type="button"
                                onClick={() => { setPasteText(''); setPasteError(''); setPasteModal({ participantIdx: idx, kind: 'injection', source: 'pm' }); }}
                                className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium border border-neutral-200 text-neutral-500 hover:border-amber-300 hover:bg-amber-50 hover:text-amber-700 transition-all"
                                title="Coller les valeurs quart-horaires"
                              >
                                <ClipboardPaste className="w-3 h-3" />Coller
                              </button>
                            </div>
                          </div>
                        )}
                        <div className="grid grid-cols-2 gap-2">
                          <div className="relative">
                            <label className="block text-[11px] text-neutral-500 mb-1.5">Tarif vente fournisseur</label>
                            <input type="number" step="0.001" value={p.supplierSellRate} onChange={e => updatePmParticipant(idx, { supplierSellRate: e.target.value })} placeholder="25" className="w-full px-3 py-2 pr-12 text-xs border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-300/50" />
                            <span className="absolute right-2.5 top-[calc(50%+8px)] -translate-y-1/2 text-[10px] text-neutral-400">€/MWh</span>
                          </div>
                          <div className="relative">
                            <label className="block text-[11px] text-neutral-500 mb-1.5">Tarif vente CE</label>
                            <input type="number" step="0.001" value={p.communityBuyRate} onChange={e => updatePmParticipant(idx, { communityBuyRate: e.target.value })} placeholder="65" className="w-full px-3 py-2 pr-12 text-xs border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-300/50" />
                            <span className="absolute right-2.5 top-[calc(50%+8px)] -translate-y-1/2 text-[10px] text-neutral-400">€/MWh</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {/* Add participant button */}
              <button
                onClick={addPmParticipant}
                className="w-full py-3 rounded-xl border-2 border-dashed border-neutral-200 text-sm font-medium text-neutral-500 hover:border-[#F8BD3D] hover:text-neutral-700 hover:bg-[#F8BD3D]/5 transition-all flex items-center justify-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Ajouter un participant
              </button>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-neutral-100 flex items-center justify-between flex-shrink-0 bg-neutral-50/60">
              <span className="text-xs text-neutral-400">{pmParticipants.length} participant{pmParticipants.length !== 1 ? 's' : ''}</span>
              <div className="flex gap-3">
                <button
                  onClick={() => setParticipantManagerSim(null)}
                  className="py-2.5 px-5 rounded-xl border border-neutral-200 text-sm font-medium text-neutral-600 hover:bg-neutral-100 transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={() => savePmParticipants(false)}
                  disabled={savingPm}
                  className="py-2.5 px-5 rounded-xl border border-neutral-200 font-semibold text-sm text-neutral-700 flex items-center gap-2 transition-all hover:bg-neutral-100 disabled:opacity-60"
                >
                  {savingPm ? <><Loader2 className="w-4 h-4 animate-spin" />Sauvegarde...</> : <><Save className="w-4 h-4" />Enregistrer</>}
                </button>
                <button
                  onClick={() => savePmParticipants(true)}
                  disabled={savingPm || runningSim}
                  className="py-2.5 px-5 rounded-xl font-semibold text-sm text-neutral-900 flex items-center gap-2 transition-all hover:opacity-90 disabled:opacity-60"
                  style={{ backgroundColor: '#F8BD3D' }}
                >
                  {(savingPm || runningSim) ? <><Loader2 className="w-4 h-4 animate-spin" />{runningSim ? 'Calcul en cours...' : 'Lancement...'}</> : <><Play className="w-4 h-4" />Lancer la simulation</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}


      <ShareSimulatorModal
        isOpen={showShareSimulatorModal}
        onClose={() => setShowShareSimulatorModal(false)}
      />

      {/* ── Quick-edit simulation modal ── */}
      {quickEditSim && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-100">
              <div>
                <h3 className="text-base font-semibold text-neutral-900">Modifier la simulation</h3>
                <p className="text-xs text-neutral-500 mt-0.5">Modifiez les informations générales</p>
              </div>
              <button
                onClick={() => setQuickEditSim(null)}
                className="p-1.5 rounded-lg hover:bg-neutral-100 transition-colors text-neutral-400"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              <div>
                <label className="block text-xs font-semibold text-neutral-700 mb-1.5">Nom de la simulation</label>
                <input
                  type="text"
                  value={quickEditName}
                  onChange={e => setQuickEditName(e.target.value)}
                  placeholder="Ex : Simulation Rue de la Loi"
                  className="w-full px-3 py-2.5 text-sm border border-neutral-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-neutral-700 mb-1.5">Région</label>
                <div className="grid grid-cols-3 gap-2">
                  {(['', 'bruxelles', 'wallonie'] as const).map(r => (
                    <button
                      key={r}
                      onClick={() => { setQuickEditRegion(r); setQuickEditSharingType(''); setQuickEditSharingScope(''); }}
                      className={`px-3 py-2 rounded-xl text-xs font-semibold border-2 transition-all ${
                        quickEditRegion === r
                          ? 'border-amber-400 bg-amber-50 text-amber-800'
                          : 'border-neutral-200 text-neutral-500 hover:border-neutral-300'
                      }`}
                    >
                      {r === '' ? 'Non définie' : r === 'bruxelles' ? 'Bruxelles' : 'Wallonie'}
                    </button>
                  ))}
                </div>
              </div>

              {quickEditRegion === 'bruxelles' && (
                <div>
                  <label className="block text-xs font-semibold text-neutral-700 mb-1.5">Type de partage</label>
                  <div className="grid grid-cols-2 gap-2">
                    {(['A', 'B', 'C', 'D'] as const).map(type => (
                      <button
                        key={type}
                        onClick={() => setQuickEditSharingType(type)}
                        className={`px-3 py-2.5 rounded-xl text-xs border-2 transition-all text-left ${
                          quickEditSharingType === type
                            ? 'border-amber-400 bg-amber-50'
                            : 'border-neutral-200 hover:border-neutral-300'
                        }`}
                      >
                        <span className="font-bold text-neutral-900">Type {type}</span>
                        <span className="block text-neutral-500 mt-0.5">{SHARING_TYPE_DESCRIPTIONS[type]}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {quickEditRegion === 'wallonie' && (
                <div>
                  <label className="block text-xs font-semibold text-neutral-700 mb-1.5">Périmètre de partage</label>
                  <div className="grid grid-cols-2 gap-2">
                    {(['batiment', 'communaute'] as const).map(scope => (
                      <button
                        key={scope}
                        onClick={() => setQuickEditSharingScope(scope)}
                        className={`px-3 py-2.5 rounded-xl text-xs border-2 transition-all text-left ${
                          quickEditSharingScope === scope
                            ? 'border-amber-400 bg-amber-50'
                            : 'border-neutral-200 hover:border-neutral-300'
                        }`}
                      >
                        <span className="font-bold text-neutral-900">{scope === 'batiment' ? 'Bâtiment' : 'Communauté'}</span>
                        <span className="block text-neutral-500 mt-0.5">
                          {scope === 'batiment' ? 'Même bâtiment (−108 €/MWh)' : 'Communauté locale (−0 €/MWh)'}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

            </div>

            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-neutral-100">
              <button
                onClick={() => setQuickEditSim(null)}
                className="px-4 py-2.5 rounded-xl text-sm font-medium text-neutral-600 border border-neutral-200 hover:bg-neutral-50 transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleQuickEditSave}
                disabled={savingQuickEdit || !quickEditName.trim()}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-neutral-900 disabled:opacity-50 transition-all hover:opacity-90"
                style={{ backgroundColor: '#F8BD3D' }}
              >
                {savingQuickEdit ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

interface ParticipantMonthlyRow {
  participantId: string;
  label: string;
  type: 'consumer' | 'producer' | 'mixed' | 'prosumer' | 'consumer_prosumer';
  monthlyShared: number[];
  monthlyBought: number[];
  monthlySold: number[];
  totalShared: number;
  totalBought: number;
  totalSold: number;
  gains: number;
  commodityGains?: number;
  networkGains?: number;
  annualConsumption: number;
  annualProduction: number;
  grossConsumption: number;
  grossProduction: number;
  autoConsumption: number;
  supplierBuyRate: number;
  communityRate: number;
  address: string;
}

interface SimulationChartModalProps {
  data: { month: string; consumption: number; production: number; shared: number; gains: number; commodityGains?: number; networkGains?: number }[];
  participantMonthlyData: ParticipantMonthlyRow[];
  totalShared: number;
  totalConsumption: number;
  totalProduction: number;
  totalGrossConsumption: number;
  totalGrossProduction: number;
  totalAutoConsumption: number;
  totalGains: number;
  totalCommodityGains?: number;
  totalNetworkGains?: number;
  networkReductionRate?: number;
  hasGains: boolean;
  simName: string;
  qhData?: SimQHParticipant[];
  onClose: () => void;
}

delete (L.Icon.Default.prototype as { _getIconUrl?: unknown })._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const PARTICIPANT_COLORS = [
  '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899',
  '#06b6d4', '#84cc16', '#f97316', '#6366f1', '#14b8a6', '#e11d48',
];

function MetricTooltip({ text, position = 'above' }: { text: string; position?: 'above' | 'below' }) {
  const [visible, setVisible] = useState(false);
  return (
    <span className="relative inline-flex items-center ml-1" onMouseEnter={() => setVisible(true)} onMouseLeave={() => setVisible(false)}>
      <svg className="w-3 h-3 text-neutral-400 cursor-help" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" />
      </svg>
      {visible && (
        position === 'below' ? (
          <span className="absolute z-50 top-full left-1/2 -translate-x-1/2 mt-2 w-52 rounded-lg bg-neutral-900 text-white text-[10px] leading-relaxed px-2.5 py-2 shadow-xl pointer-events-none whitespace-normal text-left">
            {text}
            <span className="absolute bottom-full left-1/2 -translate-x-1/2 border-4 border-transparent border-b-neutral-900" />
          </span>
        ) : (
          <span className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 w-52 rounded-lg bg-neutral-900 text-white text-[10px] leading-relaxed px-2.5 py-2 shadow-xl pointer-events-none whitespace-normal text-left">
            {text}
            <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-neutral-900" />
          </span>
        )
      )}
    </span>
  );
}

function SimulationChartModal({ data, participantMonthlyData, totalShared, totalConsumption, totalProduction, totalGrossConsumption, totalGrossProduction, totalAutoConsumption, totalGains, totalCommodityGains, totalNetworkGains, networkReductionRate, hasGains, simName, qhData, onClose }: SimulationChartModalProps) {
  const fmt = (v: number) => v.toFixed(2);
  const fmtEur = (v: number) => v.toFixed(0);
  const [activeSection, setActiveSection] = useState<number>(0);
  const [geocoding, setGeocoding] = useState<boolean>(false);
  const [geocodedCoords, setGeocodedCoords] = useState<Record<string, [number, number]>>({});
  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportingXlsx, setExportingXlsx] = useState(false);
  const contentRef = React.useRef<HTMLDivElement>(null);

  const handleExportExcel = async () => {
    setExportingXlsx(true);
    try {
      const XLSX = await import('xlsx');
      const wb = XLSX.utils.book_new();

      if (qhData && qhData.length > 0) {
        const NUM_QH = 35040;
        const QH_PER_DAY = 96;
        const DAYS_PER_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

        const headers: string[] = ['Horodatage'];
        for (const p of qhData) {
          const isConsumer = p.type === 'consumer' || p.type === 'consumer_prosumer' || p.type === 'mixed';
          const isProducer = p.type === 'producer' || p.type === 'prosumer' || p.type === 'mixed';
          if (isConsumer) headers.push(`${p.label} - Conso 1/4h (kWh)`);
          if (isProducer) headers.push(`${p.label} - Prod 1/4h (kWh)`);
          headers.push(`${p.label} - Energie partagée (kWh)`);
        }

        const rows: (string | number)[][] = [headers];

        let monthIdx = 0;
        let dayInMonth = 0;
        let qhInDay = 0;
        let dayOfYear = 1;

        for (let i = 0; i < NUM_QH; i++) {
          const hour = Math.floor(qhInDay / 4);
          const minute = (qhInDay % 4) * 15;
          const timestamp = `J${String(dayOfYear).padStart(3, '0')} ${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;

          const row: (string | number)[] = [timestamp];
          for (const p of qhData) {
            const isConsumer = p.type === 'consumer' || p.type === 'consumer_prosumer' || p.type === 'mixed';
            const isProducer = p.type === 'producer' || p.type === 'prosumer' || p.type === 'mixed';
            if (isConsumer) row.push(+(p.consumption[i] ?? 0).toFixed(6));
            if (isProducer) row.push(+(p.production[i] ?? 0).toFixed(6));
            row.push(+(p.shared[i] ?? 0).toFixed(6));
          }
          rows.push(row);

          qhInDay++;
          if (qhInDay >= QH_PER_DAY) {
            qhInDay = 0;
            dayInMonth++;
            dayOfYear++;
            if (dayInMonth >= DAYS_PER_MONTH[monthIdx]) {
              dayInMonth = 0;
              if (monthIdx < 11) monthIdx++;
            }
          }
        }

        const wsQH = XLSX.utils.aoa_to_sheet(rows);
        XLSX.utils.book_append_sheet(wb, wsQH, 'Données 1-4h');
      }

      const MONTH_LABELS = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];

      const sharedMonthlyHeaders: (string | number)[] = ['Mois', ...participantMonthlyData.map(p => `${p.label} - Energie partagée (MWh)`)];
      const sharedMonthlyRows: (string | number)[][] = [sharedMonthlyHeaders];
      for (let m = 0; m < 12; m++) {
        const row: (string | number)[] = [MONTH_LABELS[m]];
        for (const p of participantMonthlyData) {
          row.push(+(p.monthlyShared[m] ?? 0).toFixed(4));
        }
        sharedMonthlyRows.push(row);
      }
      const totalRow: (string | number)[] = ['Total'];
      for (const p of participantMonthlyData) {
        totalRow.push(+p.totalShared.toFixed(4));
      }
      sharedMonthlyRows.push(totalRow);
      const wsMonthly = XLSX.utils.aoa_to_sheet(sharedMonthlyRows);
      XLSX.utils.book_append_sheet(wb, wsMonthly, 'Energie partagée mensuelle');

      const gainsMonthlyHeaders: (string | number)[] = ['Mois', ...participantMonthlyData.map(p => `${p.label} - Gains (€)`)];
      const gainsMonthlyRows: (string | number)[][] = [gainsMonthlyHeaders];
      for (let m = 0; m < 12; m++) {
        const row: (string | number)[] = [MONTH_LABELS[m]];
        for (const p of participantMonthlyData) {
          const monthShared = p.monthlyShared[m] ?? 0;
          const totalShared = p.totalShared || 0;
          const ratio = totalShared > 0 ? monthShared / totalShared : 0;
          row.push(+(p.gains * ratio).toFixed(2));
        }
        gainsMonthlyRows.push(row);
      }
      const totalGainsRow: (string | number)[] = ['Total'];
      for (const p of participantMonthlyData) {
        totalGainsRow.push(+p.gains.toFixed(2));
      }
      gainsMonthlyRows.push(totalGainsRow);
      const wsGains = XLSX.utils.aoa_to_sheet(gainsMonthlyRows);
      XLSX.utils.book_append_sheet(wb, wsGains, 'Gains mensuels');

      const globalHeaders = ['Mois', 'Consommation totale (MWh)', 'Production totale (MWh)', 'Energie partagée totale (MWh)', 'Gains totaux (€)'];
      const globalRows: (string | number)[][] = [globalHeaders];
      for (const m of data) {
        globalRows.push([m.month, +m.consumption.toFixed(4), +m.production.toFixed(4), +m.shared.toFixed(4), +m.gains.toFixed(2)]);
      }
      const wsGlobal = XLSX.utils.aoa_to_sheet(globalRows);
      XLSX.utils.book_append_sheet(wb, wsGlobal, 'Bilan mensuel');

      const safeName = simName.replace(/[^a-zA-Z0-9_\-]/g, '_');
      XLSX.writeFile(wb, `simulation_${safeName}.xlsx`);
    } catch {
      toast.error('Erreur lors de l\'export Excel');
    } finally {
      setExportingXlsx(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!contentRef.current) return;
    setExportingPdf(true);
    try {
      const { default: html2canvas } = await import('html2canvas');
      const { default: jsPDF } = await import('jspdf');
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageW = 210;
      const pageH = 297;
      const margin = 10;
      const titleH = 12;
      const availW = pageW - margin * 2;
      const availH = pageH - margin * 2 - titleH;

      // Cover page
      pdf.setFillColor(26, 26, 26);
      pdf.rect(0, 0, pageW, pageH, 'F');

      // Logo
      const logoImg = new Image();
      logoImg.crossOrigin = 'anonymous';
      await new Promise<void>((resolve) => {
        logoImg.onload = () => resolve();
        logoImg.onerror = () => resolve();
        logoImg.src = '/images/logo-v2.png';
      });
      if (logoImg.complete && logoImg.naturalWidth > 0) {
        const logoCanvas = document.createElement('canvas');
        logoCanvas.width = logoImg.naturalWidth;
        logoCanvas.height = logoImg.naturalHeight;
        const lctx = logoCanvas.getContext('2d');
        if (lctx) {
          lctx.drawImage(logoImg, 0, 0);
          const logoDataUrl = logoCanvas.toDataURL('image/png');
          const logoW = 60;
          const logoH = (logoImg.naturalHeight / logoImg.naturalWidth) * logoW;
          pdf.addImage(logoDataUrl, 'PNG', (pageW - logoW) / 2, pageH / 2 - 60, logoW, logoH);
        }
      }

      // Cover title
      pdf.setFillColor(248, 189, 61);
      pdf.rect(0, pageH / 2 - 5, pageW, 1.5, 'F');

      pdf.setFontSize(22);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(248, 189, 61);
      pdf.text('Résultats de la simulation', pageW / 2, pageH / 2 + 20, { align: 'center' });

      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(220, 220, 220);
      const coverName = simName || 'Simulation';
      pdf.text(coverName, pageW / 2, pageH / 2 + 34, { align: 'center' });

      pdf.setFontSize(9);
      pdf.setTextColor(120, 120, 120);
      pdf.text(new Date().toLocaleDateString('fr-BE', { day: '2-digit', month: 'long', year: 'numeric' }), pageW / 2, pageH - 20, { align: 'center' });

      const sectionLabels = ['Aperçu', 'Carte', 'Répartition', 'Courbes', 'Gains', 'Par participant'];
      const originalSection = activeSection;

      for (let idx = 0; idx < sectionLabels.length; idx++) {
        setActiveSection(idx);
        await new Promise(r => setTimeout(r, 3000));

        const canvas = await html2canvas(contentRef.current!, {
          scale: 2,
          useCORS: true,
          backgroundColor: '#ffffff',
          logging: false,
        });

        pdf.addPage();

        pdf.setFontSize(11);
        pdf.setTextColor(30, 30, 30);
        pdf.setFont('helvetica', 'bold');
        pdf.text(sectionLabels[idx], margin, margin + 7);

        const imgH = (canvas.height * availW) / canvas.width;
        let remainingH = imgH;
        let srcY = 0;
        let firstSlice = true;

        while (remainingH > 0) {
          const sliceH = Math.min(remainingH, firstSlice ? availH : pageH - margin * 2);
          const posY = firstSlice ? margin + titleH : margin;

          const sliceCanvas = document.createElement('canvas');
          const ratio = canvas.width / availW;
          sliceCanvas.width = canvas.width;
          sliceCanvas.height = sliceH * ratio;
          const ctx = sliceCanvas.getContext('2d');
          if (ctx) {
            ctx.drawImage(canvas, 0, srcY * ratio, canvas.width, sliceH * ratio, 0, 0, canvas.width, sliceH * ratio);
          }
          pdf.addImage(sliceCanvas.toDataURL('image/png'), 'PNG', margin, posY, availW, sliceH);
          srcY += sliceH;
          remainingH -= sliceH;
          if (remainingH > 0) {
            pdf.addPage();
            pdf.setFontSize(9);
            pdf.setTextColor(120, 120, 120);
            pdf.setFont('helvetica', 'normal');
            pdf.text(`${sectionLabels[idx]} (suite)`, margin, 8);
          }
          firstSlice = false;
        }
      }

      setActiveSection(originalSection);
      const fileName = simName ? `simulation_${simName.replace(/[^a-zA-Z0-9]/g, '_')}.pdf` : 'simulation_resultats.pdf';
      pdf.save(fileName);
    } catch (err) {
      console.error('Erreur export PDF:', err);
    } finally {
      setExportingPdf(false);
    }
  };

  const grossCons = totalGrossConsumption > 0 ? totalGrossConsumption : totalConsumption;
  const grossProd = totalGrossProduction > 0 ? totalGrossProduction : totalProduction;
  const autoCons = totalAutoConsumption ?? 0;

  const selfSufficiencyBefore = grossCons > 0 ? (autoCons / grossCons) * 100 : 0;
  const selfSufficiencyAfter = grossCons > 0 ? ((autoCons + totalShared) / grossCons) * 100 : 0;
  const selfConsumptionBefore = grossProd > 0 ? (autoCons / grossProd) * 100 : 0;
  const selfConsumptionAfter = grossProd > 0 ? ((autoCons + totalShared) / grossProd) * 100 : 0;

  const consumersPieData = participantMonthlyData
    .filter(p => p.annualConsumption > 0)
    .map((p, i) => ({ name: p.label, value: p.annualConsumption, color: PARTICIPANT_COLORS[i % PARTICIPANT_COLORS.length] }));

  const producersPieData = participantMonthlyData
    .filter(p => p.annualProduction > 0)
    .map((p, i) => ({ name: p.label, value: p.annualProduction, color: PARTICIPANT_COLORS[i % PARTICIPANT_COLORS.length] }));

  const sections = [
    { label: 'Aperçu', icon: Users },
    { label: 'Carte', icon: MapPin },
    { label: 'Répartition', icon: BarChart3 },
    { label: 'Courbes', icon: TrendingUp },
    { label: 'Gains', icon: Receipt },
    { label: 'Par participant', icon: Calculator },
  ];

  const CustomTooltip = ({ active, payload, label }: { active?: boolean; payload?: { name: string; value: number; color: string }[]; label?: string }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-white border border-neutral-200 rounded-xl shadow-lg px-3 py-2.5 text-xs">
        <p className="font-semibold text-neutral-700 mb-1.5">{label}</p>
        {payload.map(entry => (
          <div key={entry.name} className="flex items-center gap-2 mb-0.5">
            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: entry.color }} />
            <span className="text-neutral-500">{entry.name} :</span>
            <span className="font-semibold text-neutral-800">{fmt(entry.value)} MWh</span>
          </div>
        ))}
      </div>
    );
  };

  const geocodeAddresses = async () => {
    setGeocoding(true);
    const results: Record<string, [number, number]> = {};
    for (const p of participantMonthlyData) {
      if (!p.address) continue;
      try {
        const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(p.address)}&limit=1`);
        const json = await res.json();
        if (json?.[0]) {
          results[p.participantId] = [parseFloat(json[0].lat), parseFloat(json[0].lon)];
        }
      } catch {}
    }
    setGeocodedCoords(results);
    setGeocoding(false);
  };

  React.useEffect(() => {
    if (activeSection === 1 && Object.keys(geocodedCoords).length === 0) {
      geocodeAddresses();
    }
  }, [activeSection]);

  return (
    <div className="fixed inset-0 z-[70] flex flex-col bg-white">
      <div
        className="relative flex flex-col h-full w-full"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-emerald-100 rounded-xl flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-neutral-900">Résultats de la simulation</h3>
              {simName && <p className="text-xs text-neutral-500">{simName}</p>}
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-neutral-100 transition-colors">
            <X className="w-4 h-4 text-neutral-500" />
          </button>
        </div>

        <div className="flex border-b border-neutral-100 flex-shrink-0 overflow-x-auto">
          {sections.map((s, i) => {
            const Icon = s.icon;
            return (
              <button
                key={i}
                onClick={() => setActiveSection(i)}
                className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-medium border-b-2 whitespace-nowrap transition-colors ${
                  activeSection === i
                    ? 'border-emerald-500 text-emerald-700 bg-emerald-50/50'
                    : 'border-transparent text-neutral-500 hover:text-neutral-700 hover:bg-neutral-50'
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {s.label}
              </button>
            );
          })}
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5" ref={contentRef}>

          {activeSection === 0 && (
            <>
              <p className="text-xs text-neutral-400 -mb-2">Indicateurs clés de la communauté : consommation brute, injection, partage, autoconsommation et gains.</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="rounded-xl bg-blue-50 border border-blue-100 px-4 py-3">
                  <p className="text-[10px] text-blue-500 font-semibold uppercase tracking-wide mb-0.5 flex items-center">
                    Consommation brute
                    <MetricTooltip position="below" text="Consommation totale avant autoconsommation. Pour les participants avec installation PV, il s'agit de la consommation sur site avant déduction de la production locale." />
                  </p>
                  <p className="text-base font-bold text-blue-900">{fmt(grossCons)} <span className="text-xs font-medium">MWh</span></p>
                  {autoCons > 0 && <p className="text-[10px] text-blue-600 mt-0.5">dont {fmt(autoCons)} MWh autoconsommés</p>}
                </div>
                <div className="rounded-xl bg-amber-50 border border-amber-100 px-4 py-3">
                  <p className="text-[10px] text-amber-500 font-semibold uppercase tracking-wide mb-0.5 flex items-center">
                    Injection
                    <MetricTooltip position="below" text="Énergie totale injectée dans le réseau par les producteurs et les participants avec PV, après déduction de l'autoconsommation locale sur site." />
                  </p>
                  <p className="text-base font-bold text-amber-900">{fmt(totalProduction)} <span className="text-xs font-medium">MWh</span></p>
                  {grossProd > totalProduction && <p className="text-[10px] text-amber-600 mt-0.5">prod. brute : {fmt(grossProd)} MWh</p>}
                </div>
                <div className="rounded-xl bg-emerald-50 border border-emerald-100 px-4 py-3">
                  <p className="text-[10px] text-emerald-600 font-semibold uppercase tracking-wide mb-0.5 flex items-center">
                    Partagée / an
                    <MetricTooltip position="below" text="Énergie effectivement partagée entre les membres de la communauté. Calculée comme le minimum entre l'injection disponible et la demande résiduelle à chaque quart d'heure." />
                  </p>
                  <p className="text-base font-bold text-emerald-900">{fmt(totalShared)} <span className="text-xs font-medium">MWh</span></p>
                </div>
                {hasGains && (
                  <div className="rounded-xl bg-green-50 border border-green-200 px-4 py-3">
                    <p className="text-[10px] text-green-600 font-semibold uppercase tracking-wide mb-0.5 flex items-center">
                      Gains / an
                      <MetricTooltip position="below" text="Gains financiers totaux estimés pour la communauté. Inclut les économies sur la commodité (tarif fournisseur − tarif communauté) et les réductions sur les frais de réseau." />
                    </p>
                    <p className="text-base font-bold text-green-900">{fmtEur(totalGains)} <span className="text-xs font-medium">€</span></p>
                    {(totalNetworkGains ?? 0) > 0 && (
                      <div className="mt-1 space-y-0.5">
                        <p className="text-[10px] text-green-700">Commodité : {fmtEur(totalCommodityGains ?? 0)} €</p>
                        <p className="text-[10px] text-green-700">Réseau (−{networkReductionRate} €/MWh) : {fmtEur(totalNetworkGains ?? 0)} €</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-xl border border-neutral-200 bg-white px-4 py-3">
                  <p className="text-[10px] text-neutral-500 font-semibold uppercase tracking-wide mb-2 flex items-center">
                    Taux d'autosuffisance
                    <MetricTooltip text="Part de la consommation brute totale couverte localement. Avant partage : autoconsommation seule. Après partage : autoconsommation + énergie partagée. Base : consommation brute (avant autoconso)." />
                  </p>
                  <div className="space-y-2">
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] text-neutral-400">Avant partage</span>
                        <span className="text-sm font-bold text-neutral-900">{selfSufficiencyBefore.toFixed(1)}<span className="text-[10px] font-medium ml-0.5">%</span></span>
                      </div>
                      <div className="h-1.5 rounded-full bg-neutral-100 overflow-hidden">
                        <div className="h-full rounded-full bg-neutral-400 transition-all" style={{ width: `${Math.min(selfSufficiencyBefore, 100)}%` }} />
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] text-neutral-400">Après partage</span>
                        <span className="text-sm font-bold text-emerald-700">{selfSufficiencyAfter.toFixed(1)}<span className="text-[10px] font-medium ml-0.5">%</span></span>
                      </div>
                      <div className="h-1.5 rounded-full bg-neutral-100 overflow-hidden">
                        <div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${Math.min(selfSufficiencyAfter, 100)}%` }} />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="rounded-xl border border-neutral-200 bg-white px-4 py-3">
                  <p className="text-[10px] text-neutral-500 font-semibold uppercase tracking-wide mb-2 flex items-center">
                    Taux d'autoconsommation
                    <MetricTooltip text="Part de la production brute totale consommée localement. Avant partage : autoconsommation sur site seule. Après partage : autoconsommation + énergie partagée. Base : production brute (avant autoconso)." />
                  </p>
                  <div className="space-y-2">
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] text-neutral-400">Avant partage</span>
                        <span className="text-sm font-bold text-neutral-900">{selfConsumptionBefore.toFixed(1)}<span className="text-[10px] font-medium ml-0.5">%</span></span>
                      </div>
                      <div className="h-1.5 rounded-full bg-neutral-100 overflow-hidden">
                        <div className="h-full rounded-full bg-neutral-400 transition-all" style={{ width: `${Math.min(selfConsumptionBefore, 100)}%` }} />
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] text-neutral-400">Après partage</span>
                        <span className="text-sm font-bold text-amber-700">{selfConsumptionAfter.toFixed(1)}<span className="text-[10px] font-medium ml-0.5">%</span></span>
                      </div>
                      <div className="h-1.5 rounded-full bg-neutral-100 overflow-hidden">
                        <div className="h-full rounded-full bg-amber-400 transition-all" style={{ width: `${Math.min(selfConsumptionAfter, 100)}%` }} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="overflow-x-auto rounded-xl border border-neutral-100">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-neutral-50 border-b border-neutral-100">
                      <th className="px-3 py-2.5 text-left font-semibold text-neutral-500">Participant</th>
                      <th className="px-3 py-2.5 text-center font-semibold text-neutral-500">Type</th>
                      <th className="px-3 py-2.5 text-right font-semibold text-neutral-500">Conso. brute</th>
                      <th className="px-3 py-2.5 text-right font-semibold text-neutral-500">Autoconso.</th>
                      <th className="px-3 py-2.5 text-right font-semibold text-neutral-500">Prod. brute</th>
                      <th className="px-3 py-2.5 text-right font-semibold text-neutral-500">Injection</th>
                      <th className="px-3 py-2.5 text-right font-semibold text-neutral-500">Partagée</th>
                    </tr>
                  </thead>
                  <tbody>
                    {participantMonthlyData.map((p, i) => (
                      <tr key={p.participantId} className={i % 2 === 0 ? 'bg-white' : 'bg-neutral-50/50'}>
                        <td className="px-3 py-2.5 font-medium text-neutral-800">
                          <div className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: PARTICIPANT_COLORS[i % PARTICIPANT_COLORS.length] }} />
                            {p.label}
                          </div>
                        </td>
                        <td className="px-3 py-2.5 text-center">
                          {(() => {
                            const isEffectivelyMixed = p.grossConsumption > 0 && p.grossProduction > 0;
                            if (isEffectivelyMixed) {
                              return (
                                <div className="flex items-center justify-center gap-1 flex-wrap">
                                  <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-100 text-blue-700">Conso.</span>
                                  <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-700">Prod.</span>
                                </div>
                              );
                            }
                            const isConsumer = p.type === 'consumer' || p.type === 'consumer_prosumer' || p.type === 'mixed';
                            return (
                              <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold ${isConsumer ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>
                                {isConsumer ? 'Consommateur' : 'Producteur'}
                              </span>
                            );
                          })()}
                        </td>
                        <td className="px-3 py-2.5 text-right text-neutral-600 tabular-nums">{p.grossConsumption > 0 ? fmt(p.grossConsumption) : '—'}</td>
                        <td className="px-3 py-2.5 text-right text-neutral-600 tabular-nums">{p.autoConsumption > 0 ? fmt(p.autoConsumption) : '—'}</td>
                        <td className="px-3 py-2.5 text-right text-neutral-600 tabular-nums">{p.grossProduction > 0 ? fmt(p.grossProduction) : '—'}</td>
                        <td className="px-3 py-2.5 text-right text-neutral-600 tabular-nums">{p.annualProduction > 0 ? fmt(p.annualProduction) : '—'}</td>
                        <td className="px-3 py-2.5 text-right text-neutral-600 tabular-nums">{p.totalShared > 0 ? fmt(p.totalShared) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {activeSection === 1 && (
            <div className="space-y-4">
              <p className="text-xs text-neutral-400">Localisation géographique des participants et visualisation sur carte interactive.</p>
              {geocoding && (
                <div className="flex items-center justify-center gap-2 py-6 text-xs text-neutral-500">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Géocodage des adresses...
                </div>
              )}
              <div className="overflow-x-auto rounded-xl border border-neutral-100">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-neutral-50 border-b border-neutral-100">
                      <th className="px-3 py-2.5 text-left font-semibold text-neutral-500">Participant</th>
                      <th className="px-3 py-2.5 text-center font-semibold text-neutral-500">Type</th>
                      <th className="px-3 py-2.5 text-left font-semibold text-neutral-500">Adresse</th>
                      <th className="px-3 py-2.5 text-center font-semibold text-neutral-500">Localisation</th>
                    </tr>
                  </thead>
                  <tbody>
                    {participantMonthlyData.map((p, i) => {
                      const coords = geocodedCoords[p.participantId];
                      return (
                        <tr key={p.participantId} className={i % 2 === 0 ? 'bg-white' : 'bg-neutral-50/50'}>
                          <td className="px-3 py-2.5 font-medium text-neutral-800">
                            <div className="flex items-center gap-2">
                              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: PARTICIPANT_COLORS[i % PARTICIPANT_COLORS.length] }} />
                              {p.label}
                            </div>
                          </td>
                          <td className="px-3 py-2.5 text-center">
                            {p.grossConsumption > 0 && p.grossProduction > 0 ? (
                              <div className="flex items-center justify-center gap-1 flex-wrap">
                                <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-100 text-blue-700">Conso.</span>
                                <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-700">Prod.</span>
                              </div>
                            ) : (
                              <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold ${
                                p.type === 'consumer' || p.type === 'consumer_prosumer' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'
                              }`}>
                                {p.type === 'consumer' || p.type === 'consumer_prosumer' ? 'Consommateur' : 'Producteur'}
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2.5 text-neutral-600">{p.address || <span className="text-neutral-300 italic">Non renseignée</span>}</td>
                          <td className="px-3 py-2.5 text-center">
                            {coords ? (
                              <a
                                href={`https://www.openstreetmap.org/?mlat=${coords[0]}&mlon=${coords[1]}#map=16/${coords[0]}/${coords[1]}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors text-[10px] font-medium"
                              >
                                <MapPin className="w-3 h-3" />
                                Voir
                              </a>
                            ) : (
                              <span className="text-neutral-300 text-[10px]">{p.address ? (geocoding ? '...' : 'Non trouvé') : '—'}</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {Object.keys(geocodedCoords).length > 0 && (
                <div className="rounded-xl border border-neutral-200 overflow-hidden" style={{ height: 380 }}>
                  {(() => {
                    const coords = Object.values(geocodedCoords);
                    const lats = coords.map(c => c[0]);
                    const lons = coords.map(c => c[1]);
                    const centerLat = (Math.min(...lats) + Math.max(...lats)) / 2;
                    const centerLon = (Math.min(...lons) + Math.max(...lons)) / 2;
                    return (
                      <MapContainer center={[centerLat, centerLon]} zoom={12} style={{ height: '100%', width: '100%' }} scrollWheelZoom={false}>
                        <TileLayer
                          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                        {Object.entries(geocodedCoords).map(([id, c]) => {
                          const p = participantMonthlyData.find(pp => pp.participantId === id);
                          return (
                            <Marker key={id} position={[c[0], c[1]]}>
                              <Popup>
                                <strong>{p?.label || id}</strong>
                                {p?.address && <><br />{p.address}</>}
                              </Popup>
                            </Marker>
                          );
                        })}
                      </MapContainer>
                    );
                  })()}
                </div>
              )}
              {!geocoding && participantMonthlyData.every(p => !p.address) && (
                <div className="flex items-center justify-center gap-2 py-8 text-xs text-neutral-400">
                  <MapPin className="w-4 h-4" />
                  Aucune adresse renseignée — ajoutez les adresses dans le formulaire de simulation.
                </div>
              )}
            </div>
          )}

          {activeSection === 2 && (
            <div className="space-y-4">
              <p className="text-xs text-neutral-400">Répartition de l'énergie partagée entre les consommateurs et les producteurs de la communauté.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {consumersPieData.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-3 text-center">Consommateurs — Répartition</h4>
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie data={consumersPieData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={2}>
                        {consumersPieData.map((entry, i) => (
                          <Cell key={i} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v: number) => [`${fmt(v)} MWh`, '']} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="flex flex-wrap justify-center gap-x-3 gap-y-1 mt-2">
                    {consumersPieData.map((e, i) => {
                      const total = consumersPieData.reduce((a, b) => a + b.value, 0);
                      return (
                        <div key={i} className="flex items-center gap-1.5 text-[10px] text-neutral-600">
                          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: e.color }} />
                          {e.name} ({total > 0 ? ((e.value / total) * 100).toFixed(1) : 0}%)
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {producersPieData.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-3 text-center">Producteurs — Répartition</h4>
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie data={producersPieData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={2}>
                        {producersPieData.map((entry, i) => (
                          <Cell key={i} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v: number) => [`${fmt(v)} MWh`, '']} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="flex flex-wrap justify-center gap-x-3 gap-y-1 mt-2">
                    {producersPieData.map((e, i) => {
                      const total = producersPieData.reduce((a, b) => a + b.value, 0);
                      return (
                        <div key={i} className="flex items-center gap-1.5 text-[10px] text-neutral-600">
                          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: e.color }} />
                          {e.name} ({total > 0 ? ((e.value / total) * 100).toFixed(1) : 0}%)
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              </div>
            </div>
          )}

          {activeSection === 3 && (
            <div className="flex flex-col">
              <p className="text-xs text-neutral-400 mb-3">Evolution mensuelle de la consommation, de l'injection et de l'énergie partagée sur l'année.</p>
              <div className="grid grid-cols-2 gap-3" style={{ height: '460px' }}>
                <div className="bg-neutral-50 rounded-xl p-3 flex flex-col min-h-0">
                  <h4 className="text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-2 shrink-0">Consommation nette mensuelle (MWh)</h4>
                  <div className="flex-1 min-h-0">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                        <defs>
                          <linearGradient id="gradCons" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.15} />
                            <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                        <XAxis dataKey="month" tick={{ fontSize: 9, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontSize: 9, fill: '#9ca3af' }} axisLine={false} tickLine={false} tickFormatter={v => `${v.toFixed(0)}`} width={30} />
                        <Tooltip content={<CustomTooltip />} />
                        <Area type="monotone" dataKey="consumption" name="Consommation" stroke="#3b82f6" strokeWidth={1.5} fill="url(#gradCons)" dot={false} activeDot={{ r: 3, fill: '#3b82f6' }} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                <div className="bg-neutral-50 rounded-xl p-3 flex flex-col min-h-0">
                  <h4 className="text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-2 shrink-0">Injection mensuelle (MWh)</h4>
                  <div className="flex-1 min-h-0">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                        <defs>
                          <linearGradient id="gradProd" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.15} />
                            <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                        <XAxis dataKey="month" tick={{ fontSize: 9, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontSize: 9, fill: '#9ca3af' }} axisLine={false} tickLine={false} tickFormatter={v => `${v.toFixed(0)}`} width={30} />
                        <Tooltip content={<CustomTooltip />} />
                        <Area type="monotone" dataKey="production" name="Injection" stroke="#f59e0b" strokeWidth={1.5} fill="url(#gradProd)" dot={false} activeDot={{ r: 3, fill: '#f59e0b' }} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                <div className="bg-neutral-50 rounded-xl p-3 flex flex-col min-h-0">
                  <h4 className="text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-2 shrink-0">Energie partagée mensuelle (MWh)</h4>
                  <div className="flex-1 min-h-0">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                        <defs>
                          <linearGradient id="gradShared" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#10b981" stopOpacity={0.2} />
                            <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                        <XAxis dataKey="month" tick={{ fontSize: 9, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontSize: 9, fill: '#9ca3af' }} axisLine={false} tickLine={false} tickFormatter={v => `${v.toFixed(0)}`} width={30} />
                        <Tooltip content={<CustomTooltip />} />
                        <Area type="monotone" dataKey="shared" name="Energie partagée" stroke="#10b981" strokeWidth={1.5} fill="url(#gradShared)" dot={false} activeDot={{ r: 3, fill: '#10b981' }} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                <div className="bg-neutral-50 rounded-xl p-3 flex flex-col min-h-0">
                  <h4 className="text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-2 shrink-0">Vue combinée (MWh)</h4>
                  <div className="flex-1 min-h-0">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                        <defs>
                          <linearGradient id="gc2" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.1} />
                            <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                          </linearGradient>
                          <linearGradient id="gp2" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.1} />
                            <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                          </linearGradient>
                          <linearGradient id="gs2" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#10b981" stopOpacity={0.15} />
                            <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                        <XAxis dataKey="month" tick={{ fontSize: 9, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontSize: 9, fill: '#9ca3af' }} axisLine={false} tickLine={false} tickFormatter={v => `${v.toFixed(0)}`} width={30} />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend wrapperStyle={{ fontSize: '10px', paddingTop: '4px' }} />
                        <Area type="monotone" dataKey="consumption" name="Conso. nette" stroke="#3b82f6" strokeWidth={1.5} fill="url(#gc2)" dot={false} />
                        <Area type="monotone" dataKey="production" name="Injection" stroke="#f59e0b" strokeWidth={1.5} fill="url(#gp2)" dot={false} />
                        <Area type="monotone" dataKey="shared" name="Energie partagée" stroke="#10b981" strokeWidth={1.5} fill="url(#gs2)" dot={false} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeSection === 4 && (
            <div className="space-y-3">
              <p className="text-xs text-neutral-400">Tableau mensuel des volumes d'énergie et des gains financiers estimés pour la communauté.</p>
              <div className="overflow-x-auto rounded-xl border border-neutral-100">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-neutral-50 border-b border-neutral-100">
                    <th className="px-3 py-2.5 text-left font-semibold text-neutral-500">Mois</th>
                    <th className="px-3 py-2.5 text-right font-semibold text-neutral-500">Consommation (MWh)</th>
                    <th className="px-3 py-2.5 text-right font-semibold text-neutral-500">Injection (MWh)</th>
                    <th className="px-3 py-2.5 text-right font-semibold text-neutral-500">Partagé (MWh)</th>
                    {hasGains && <th className="px-3 py-2.5 text-right font-semibold text-neutral-500">Gains commodité (€)</th>}
                    {hasGains && (totalNetworkGains ?? 0) > 0 && <th className="px-3 py-2.5 text-right font-semibold text-neutral-500">Gains réseau (€)</th>}
                    {hasGains && <th className="px-3 py-2.5 text-right font-semibold text-neutral-700">Total gains (€)</th>}
                  </tr>
                </thead>
                <tbody>
                  {data.map((row, i) => (
                    <tr key={row.month} className={i % 2 === 0 ? 'bg-white' : 'bg-neutral-50/50'}>
                      <td className="px-3 py-2 font-medium text-neutral-700">{row.month}</td>
                      <td className="px-3 py-2 text-right text-neutral-600 tabular-nums">{fmt(row.consumption)}</td>
                      <td className="px-3 py-2 text-right text-neutral-600 tabular-nums">{fmt(row.production)}</td>
                      <td className="px-3 py-2 text-right text-neutral-600 tabular-nums">{fmt(row.shared)}</td>
                      {hasGains && <td className="px-3 py-2 text-right tabular-nums text-neutral-600">{fmtEur(row.commodityGains ?? row.gains)} €</td>}
                      {hasGains && (totalNetworkGains ?? 0) > 0 && <td className="px-3 py-2 text-right tabular-nums text-neutral-600">{fmtEur(row.networkGains ?? 0)} €</td>}
                      {hasGains && <td className="px-3 py-2 text-right font-semibold tabular-nums" style={{ color: '#15803d' }}>{fmtEur(row.gains)} €</td>}
                    </tr>
                  ))}
                  <tr className="bg-neutral-100 border-t border-neutral-200 font-bold">
                    <td className="px-3 py-2.5 text-neutral-800">Total annuel</td>
                    <td className="px-3 py-2.5 text-right text-neutral-800 tabular-nums">{fmt(totalConsumption)}</td>
                    <td className="px-3 py-2.5 text-right text-neutral-800 tabular-nums">{fmt(totalProduction)}</td>
                    <td className="px-3 py-2.5 text-right text-neutral-800 tabular-nums">{fmt(totalShared)}</td>
                    {hasGains && <td className="px-3 py-2.5 text-right tabular-nums text-neutral-700">{fmtEur(totalCommodityGains ?? totalGains)} €</td>}
                    {hasGains && (totalNetworkGains ?? 0) > 0 && <td className="px-3 py-2.5 text-right tabular-nums text-neutral-700">{fmtEur(totalNetworkGains ?? 0)} €</td>}
                    {hasGains && <td className="px-3 py-2.5 text-right tabular-nums" style={{ color: '#14532d' }}>{fmtEur(totalGains)} €</td>}
                  </tr>
                </tbody>
              </table>
              </div>
            </div>
          )}

          {activeSection === 5 && (() => {
            const MONTHS = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
            // Buyers: participants that receive energy from the community (have monthlyBought > 0)
            const buyers = participantMonthlyData.filter(r => r.totalBought > 0);
            // Sellers: participants that inject energy into the community (have monthlySold > 0)
            const sellers = participantMonthlyData.filter(r => r.totalSold > 0);

            const renderTable = (
              rows: typeof participantMonthlyData,
              getMonthly: (r: typeof participantMonthlyData[0]) => number[],
              getTotal: (r: typeof participantMonthlyData[0]) => number,
              colorScheme: 'blue' | 'amber'
            ) => {
              const allIdx = rows.map(r => participantMonthlyData.indexOf(r));
              const isBuyer = colorScheme === 'blue';
              const headerCls = isBuyer ? 'bg-blue-50 border-blue-100' : 'bg-amber-50 border-amber-100';
              const totalRowCls = isBuyer ? 'bg-blue-50/80 border-blue-100' : 'bg-amber-50/80 border-amber-100';
              const totalTextCls = isBuyer ? 'text-blue-900' : 'text-amber-900';
              return (
                <div className="overflow-x-auto rounded-xl border border-neutral-100">
                  <table className="w-full text-xs min-w-[700px]">
                    <thead>
                      <tr className={`${headerCls} border-b`}>
                        <th className={`px-3 py-2.5 text-left font-semibold text-neutral-500 sticky left-0 z-10 min-w-[130px] ${headerCls}`}>Participant</th>
                        <th className="px-2 py-2.5 text-center font-semibold text-neutral-400 text-[10px]">Type</th>
                        {MONTHS.map(m => (
                          <th key={m} className="px-2 py-2.5 text-right font-semibold text-neutral-500">{m}</th>
                        ))}
                        <th className="px-3 py-2.5 text-right font-semibold text-neutral-700">Total MWh</th>
                        {hasGains && <th className="px-3 py-2.5 text-right font-semibold text-neutral-500">Gains (€)</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row, i) => {
                        const globalIdx = allIdx[i];
                        const monthly = getMonthly(row);
                        const total = getTotal(row);
                        return (
                          <tr key={row.participantId} className={i % 2 === 0 ? 'bg-white' : 'bg-neutral-50/50'}>
                            <td className="px-3 py-2 sticky left-0 bg-inherit z-10">
                              <div className="flex items-center gap-2 font-medium text-neutral-800">
                                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: PARTICIPANT_COLORS[globalIdx % PARTICIPANT_COLORS.length] }} />
                                {row.label}
                              </div>
                            </td>
                            <td className="px-2 py-2 text-center">
                              {row.grossConsumption > 0 && row.grossProduction > 0 ? (
                                <div className="flex items-center justify-center gap-0.5 flex-wrap">
                                  <span className="inline-block px-1 py-0.5 rounded text-[9px] font-bold bg-blue-100 text-blue-700">C</span>
                                  <span className="inline-block px-1 py-0.5 rounded text-[9px] font-bold bg-amber-100 text-amber-700">P</span>
                                </div>
                              ) : (
                                <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-bold ${row.grossConsumption > 0 ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>
                                  {row.grossConsumption > 0 ? 'C' : 'P'}
                                </span>
                              )}
                            </td>
                            {monthly.map((v, mi) => (
                              <td key={mi} className="px-2 py-2 text-right text-neutral-600 tabular-nums">{v.toFixed(1)}</td>
                            ))}
                            <td className="px-3 py-2 text-right font-semibold text-neutral-800 tabular-nums">{total.toFixed(2)}</td>
                            {hasGains && (
                              <td className="px-3 py-2 text-right font-semibold tabular-nums" style={{ color: isBuyer ? '#1d4ed8' : '#b45309' }}>{row.gains.toFixed(0)} €</td>
                            )}
                          </tr>
                        );
                      })}
                      <tr className={`${totalRowCls} border-t font-bold`}>
                        <td className={`px-3 py-2.5 ${totalTextCls} sticky left-0 z-10 ${totalRowCls}`}>Total</td>
                        <td className="px-2 py-2.5" />
                        {MONTHS.map((_, mi) => (
                          <td key={mi} className="px-2 py-2.5 text-right text-neutral-700 tabular-nums">
                            {rows.reduce((acc, r) => acc + getMonthly(r)[mi], 0).toFixed(1)}
                          </td>
                        ))}
                        <td className={`px-3 py-2.5 text-right tabular-nums ${totalTextCls}`}>{rows.reduce((acc, r) => acc + getTotal(r), 0).toFixed(2)}</td>
                        {hasGains && (
                          <td className={`px-3 py-2.5 text-right tabular-nums font-bold ${totalTextCls}`}>{rows.reduce((acc, r) => acc + r.gains, 0).toFixed(0)} €</td>
                        )}
                      </tr>
                    </tbody>
                  </table>
                </div>
              );
            };

            return (
              <div className="space-y-5">
                <p className="text-xs text-neutral-400">Volumes mensuels échangés au sein de la communauté, par participant.</p>
                {buyers.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-xs font-semibold text-blue-700 uppercase tracking-wide flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-blue-500 inline-block" />
                      Volumes achetés à la communauté
                    </h4>
                    {renderTable(buyers, r => r.monthlyBought, r => r.totalBought, 'blue')}
                  </div>
                )}
                {sellers.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-xs font-semibold text-amber-700 uppercase tracking-wide flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full bg-amber-500 inline-block" />
                      Volumes vendus à la communauté
                    </h4>
                    {renderTable(sellers, r => r.monthlySold, r => r.totalSold, 'amber')}
                  </div>
                )}
              </div>
            );
          })()}

        </div>

        <div className="px-6 py-3 border-t border-neutral-100 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <button
              onClick={handleDownloadPdf}
              disabled={exportingPdf}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-emerald-600 text-sm font-medium text-white hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {exportingPdf ? (
                <><Loader2 className="w-4 h-4 animate-spin" />Export en cours...</>
              ) : (
                <><Download className="w-4 h-4" />Télécharger PDF</>
              )}
            </button>
            <button
              onClick={handleExportExcel}
              disabled={exportingXlsx}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-emerald-50 border border-emerald-200 text-sm font-medium text-emerald-700 hover:bg-emerald-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {exportingXlsx ? (
                <><Loader2 className="w-4 h-4 animate-spin" />Export Excel...</>
              ) : (
                <><FileText className="w-4 h-4" />Exporter Excel</>
              )}
            </button>
          </div>
          <button
            onClick={onClose}
            className="px-5 py-2.5 rounded-xl bg-neutral-100 text-sm font-medium text-neutral-700 hover:bg-neutral-200 transition-colors"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}
